// src/data/legalResourceData.js
// Curated directory of immigration legal aid organizations, hotlines,
// know-your-rights programs, and online directories.
// Last verified: February 2025

export const LAST_VERIFIED = 'February 2025';

// ─── Instant Help Services (on-demand attorney connections) ─────
export const INSTANT_HELP_SERVICES = [
  {
    id: 'instant-turnsignl',
    name: 'TurnSignl',
    type: 'video',
    phone: null,
    url: 'https://turnsignl.com',
    available24_7: true,
  },
  {
    id: 'instant-goodcall',
    name: 'GoodCall',
    type: 'phone',
    phone: '1-855-274-0205',
    url: 'https://goodcall.nyc',
    available24_7: true,
  },
];

export const RESOURCE_CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'hotline', label: 'Hotlines' },
  { id: 'legal-aid', label: 'Legal Aid' },
  { id: 'know-your-rights', label: 'Know Your Rights' },
  { id: 'directory', label: 'Directories' },
  { id: 'community', label: 'Community' },
];

export const NATIONAL_RESOURCES = {
  hotlines: [
    {
      id: 'nat-h-1',
      name: 'National Immigration Detention Hotline',
      description: 'Free hotline for people in immigration detention. Dial 9233# from a detention facility phone. Family/friends call the main number.',
      phone: '1-209-757-3733',
      url: 'https://www.freedomforimmigrants.org/hotline',
      category: 'hotline',
      hours: 'Mon–Fri 8am–8pm PT',
      available24_7: false,
      languages: ['English', 'Spanish'],
    },
    {
      id: 'nat-h-2',
      name: 'United We Dream — Raid Reporting Line',
      description: 'Report ICE raids and get guidance. Call or text 877877.',
      phone: '1-844-363-1423',
      url: 'https://unitedwedream.org',
      category: 'hotline',
      hours: 'Mon–Fri 9am–5pm ET',
      available24_7: false,
      languages: ['English', 'Spanish'],
    },
    {
      id: 'nat-h-3',
      name: 'Immigrant Defense Project Helpline',
      description: 'Legal assistance for immigrants and families facing detention or deportation.',
      phone: '1-212-725-6422',
      url: 'https://www.immigrantdefenseproject.org',
      category: 'hotline',
      hours: 'Tue & Thu 2–4:30pm ET',
      available24_7: false,
      languages: ['English', 'Spanish'],
    },
    {
      id: 'nat-h-4',
      name: 'RAICES Consultation Hotline',
      description: 'Legal services for immigrant families and refugees. Texas-based with national reach.',
      phone: '1-833-372-4237',
      url: 'https://raicestexas.org',
      category: 'hotline',
      hours: 'Mon–Fri 9am–5pm CT',
      available24_7: false,
      languages: ['English', 'Spanish'],
    },
    {
      id: 'nat-h-5',
      name: 'LatinoJustice PRLDEF',
      description: 'Legal advice and referrals for housing, employment, immigration, discrimination, and civil rights.',
      phone: '1-800-328-2322',
      url: 'https://www.latinojustice.org',
      category: 'hotline',
      hours: 'Mon–Fri 9am–5pm ET',
      available24_7: false,
      languages: ['English', 'Spanish'],
    },
    {
      id: 'nat-h-6',
      name: 'Immigration Equality (LGBTQ+)',
      description: 'Legal services for LGBTQ and HIV-positive immigrants.',
      phone: '1-212-714-2904',
      url: 'https://immigrationequality.org',
      category: 'hotline',
      hours: 'Mon–Fri 9am–5pm ET',
      available24_7: false,
      languages: ['English', 'Spanish'],
    },
    {
      id: 'nat-h-7',
      name: 'USCIS Contact Center',
      description: 'Government immigration case inquiries, application status, and general information.',
      phone: '1-800-375-5283',
      url: 'https://www.uscis.gov/contactcenter',
      category: 'hotline',
      hours: 'Mon–Fri 8am–8pm ET',
      available24_7: false,
      languages: ['English', 'Spanish'],
    },
    {
      id: 'nat-h-8',
      name: 'United Way 211',
      description: 'Connects to local resources including immigration services. Dial 211 or text your zip code to 898-211.',
      phone: '211',
      url: null,
      category: 'hotline',
      hours: '24/7',
      available24_7: true,
      languages: ['English', 'Spanish', '200+ via interpreters'],
    },
    {
      id: 'nat-h-9',
      name: 'National Domestic Violence Hotline',
      description: 'Includes immigrant-specific services and VAWA information for survivors.',
      phone: '1-800-799-7233',
      url: 'https://www.thehotline.org',
      category: 'hotline',
      hours: '24/7',
      available24_7: true,
      languages: ['English', 'Spanish', '200+ via interpreters'],
    },
    {
      id: 'nat-h-10',
      name: 'GoodCall — Arrest Hotline',
      description: 'Free 24/7 emergency legal hotline for people being arrested. Connects to an attorney within minutes.',
      phone: '1-855-274-0205',
      url: 'https://goodcall.nyc',
      category: 'hotline',
      hours: '24/7',
      available24_7: true,
      languages: ['English', 'Spanish'],
    },
    {
      id: 'nat-h-11',
      name: 'National Lawyers Guild — Legal Support',
      description: 'Volunteer attorneys for protest arrests and mass defense. Regional chapters provide local support.',
      phone: '1-212-679-5100',
      url: 'https://www.nlg.org',
      category: 'hotline',
      hours: 'Mon–Fri 9am–5pm ET',
      available24_7: false,
      languages: ['English', 'Spanish'],
    },
    {
      id: 'nat-h-12',
      name: 'NNIRR Immigration Hotline Directory',
      description: 'Master list of state and local immigration hotlines maintained by the National Network for Immigrant and Refugee Rights.',
      phone: null,
      url: 'https://nnirr.org/education-resources/community-resources-legal-assistance-recursos-comunitarios-asistencia-legal/immigration-hotlines-lineas-directas-de-inmigracion/',
      category: 'hotline',
      hours: 'Varies by hotline',
      available24_7: false,
      languages: ['English', 'Spanish'],
    },
    {
      id: 'nat-h-13',
      name: 'National Association for Public Defense — Rapid Response Hotlines',
      description: 'Directory of rapid response network hotlines for communities facing immigration enforcement actions.',
      phone: null,
      url: 'https://publicdefenders.us',
      category: 'hotline',
      hours: 'Varies by region',
      available24_7: false,
      languages: ['English', 'Spanish'],
    },
  ],
  organizations: [
    {
      id: 'nat-o-1',
      name: 'National Immigration Law Center (NILC)',
      description: 'Advances rights of low-income immigrants through policy advocacy and know-your-rights resources.',
      phone: '(202) 216-0261',
      url: 'https://www.nilc.org',
      category: 'legal-aid',
    },
    {
      id: 'nat-o-2',
      name: 'National Immigrant Justice Center (NIJC)',
      description: 'Legal services, policy advocacy, detention defense, asylum, and family reunification.',
      phone: null,
      url: 'https://immigrantjustice.org',
      category: 'legal-aid',
    },
    {
      id: 'nat-o-3',
      name: 'Immigrant Legal Resource Center (ILRC)',
      description: 'Training, technical assistance, policy advocacy. Runs the New Americans Campaign for citizenship.',
      phone: null,
      url: 'https://www.ilrc.org',
      category: 'legal-aid',
    },
    {
      id: 'nat-o-4',
      name: 'ACLU Immigrants\' Rights Project',
      description: 'Litigation and advocacy on immigrants\' constitutional rights. State affiliates provide local resources.',
      phone: null,
      url: 'https://www.aclu.org/issues/immigrants-rights',
      category: 'legal-aid',
    },
    {
      id: 'nat-o-5',
      name: 'American Immigration Lawyers Association (AILA)',
      description: 'Professional association of immigration lawyers. Find a qualified attorney through their directory.',
      phone: null,
      url: 'https://www.aila.org',
      category: 'legal-aid',
    },
    {
      id: 'nat-o-6',
      name: 'Catholic Legal Immigration Network (CLINIC)',
      description: 'Network of 420+ affiliates across 49 states providing immigration legal services.',
      phone: null,
      url: 'https://www.cliniclegal.org',
      category: 'legal-aid',
    },
    {
      id: 'nat-o-7',
      name: 'World Relief',
      description: 'DOJ-accredited legal services for refugees and immigrants through local offices nationwide.',
      phone: null,
      url: 'https://worldrelief.org/immigration-legal-services/',
      category: 'legal-aid',
    },
    {
      id: 'nat-o-8',
      name: 'Kids in Need of Defense (KIND)',
      description: 'Free legal representation for unaccompanied immigrant children.',
      phone: null,
      url: 'https://supportkind.org',
      category: 'legal-aid',
    },
    {
      id: 'nat-o-9',
      name: 'RedadAlertas — Community ICE Raid Reporting',
      description: 'Open-source community tool for real-time ICE raid reporting and alerts. Report sightings and receive location-based notifications.',
      phone: null,
      url: 'https://redadalertas.com',
      category: 'community',
    },
    {
      id: 'nat-o-10',
      name: 'ReadyNow! — Emergency Preparedness App',
      description: 'Free app from Human Rights First. Build a personalized emergency plan with one-tap alerts to your contacts during an ICE encounter. Available in English, Spanish, Korean, Chinese, Haitian Creole, and French.',
      phone: null,
      url: 'https://www.humanrightsfirst.org/readynow',
      category: 'community',
    },
    {
      id: 'nat-o-11',
      name: 'ICEBlock — Community ICE Sighting Reports',
      description: 'Waze-like community platform for reporting and viewing ICE sightings in your area. No personal data stored.',
      phone: null,
      url: 'https://www.iceblock.app',
      category: 'community',
    },
  ],
  directories: [
    {
      id: 'nat-d-1',
      name: 'Immigration Advocates Network — Legal Services Directory',
      description: 'The most comprehensive directory of nonprofit immigration legal services. Search by state, county, or detention facility.',
      url: 'https://www.immigrationadvocates.org/nonprofit/legaldirectory/',
      category: 'directory',
    },
    {
      id: 'nat-d-2',
      name: 'ImmigrationLawHelp.org',
      description: 'Directory of 1,000+ free or low-cost nonprofit immigration legal services providers in all 50 states.',
      url: 'https://www.immigrationlawhelp.org',
      category: 'directory',
    },
    {
      id: 'nat-d-3',
      name: 'AILA Immigration Lawyer Search',
      description: 'Find vetted immigration attorneys. All listed attorneys are AILA members with liability insurance and CLE requirements.',
      url: 'https://ailalawyer.com',
      category: 'directory',
    },
    {
      id: 'nat-d-4',
      name: 'CLINIC Affiliate Directory',
      description: 'Over 420 affiliates across 49 states. Find Catholic and non-Catholic nonprofits serving low-income immigrants.',
      url: 'https://www.cliniclegal.org/find-legal-help/affiliates/directory',
      category: 'directory',
    },
    {
      id: 'nat-d-5',
      name: 'DOJ EOIR Pro Bono Legal Service Providers',
      description: 'Government-maintained list of attorneys and organizations providing free immigration legal services.',
      url: 'https://www.justice.gov/eoir/list-pro-bono-legal-service-providers',
      category: 'directory',
    },
    {
      id: 'nat-d-6',
      name: 'ICE Online Detainee Locator (ODLS)',
      description: 'Search for a detained person by name, country of birth, or A-Number. Government website — may not update for 12-24 hours after detention.',
      url: 'https://locator.ice.gov/odls/',
      category: 'directory',
      governmentSite: true,
    },
    {
      id: 'nat-d-7',
      name: 'ABA Free Legal Answers — Immigration Portal',
      description: 'Submit immigration law questions for free and get answers from licensed pro bono attorneys through the American Bar Association.',
      url: 'https://abafreelegalanswers.org/',
      category: 'directory',
    },
    {
      id: 'nat-d-8',
      name: 'EOIR Immigration Court Case Status (ACIS)',
      description: 'Check your immigration court hearing dates and case status online using your Alien Registration Number (A-Number). Government website.',
      url: 'https://acis.eoir.justice.gov/',
      category: 'directory',
      governmentSite: true,
    },
    {
      id: 'nat-d-9',
      name: 'DOJ Recognized Organizations & Accredited Representatives',
      description: 'Official government roster of nonprofit organizations authorized by the Department of Justice to provide immigration legal services. Searchable by state and city.',
      url: 'https://www.justice.gov/eoir/recognized-organizations-and-accredited-representatives-roster-state-and-city',
      category: 'directory',
      governmentSite: true,
    },
  ],
  knowYourRights: [
    {
      id: 'nat-k-1',
      name: 'ACLU — Know Your Rights: Immigrants',
      description: 'Comprehensive guide to immigrant rights during ICE encounters at home, work, and public spaces. Available in multiple languages.',
      url: 'https://www.aclu.org/know-your-rights/immigrants-rights',
      category: 'know-your-rights',
    },
    {
      id: 'nat-k-2',
      name: 'NIJC — Know Your Rights',
      description: 'Guides covering detention, deportation, defenses, and ICE encounters.',
      url: 'https://immigrantjustice.org/for-immigrants/know-your-rights/',
      category: 'know-your-rights',
    },
    {
      id: 'nat-k-3',
      name: 'NILC — Resources',
      description: 'Resources on immigrants\' access to public benefits, understanding warrants, and rights information.',
      url: 'https://www.nilc.org/resources/',
      category: 'know-your-rights',
    },
    {
      id: 'nat-k-4',
      name: 'ILRC — Community Resources',
      description: 'Community resources and know-your-rights materials, including red card programs.',
      url: 'https://www.ilrc.org/community-resources',
      category: 'know-your-rights',
    },
    {
      id: 'nat-k-5',
      name: 'Informed Immigrant',
      description: '200+ trusted resources covering know-your-rights, family preparation, DACA, and ICE enforcement preparation.',
      url: 'https://www.informedimmigrant.com/help/',
      category: 'know-your-rights',
    },
    {
      id: 'nat-k-6',
      name: 'ACLU Mobile Justice',
      description: 'Record encounters with law enforcement with automatic upload to the ACLU. State-specific recording guidance.',
      url: 'https://www.aclu.org/issues/criminal-law-reform/reforming-police/aclu-apps-record-police-conduct',
      category: 'know-your-rights',
    },
    {
      id: 'nat-k-7',
      name: 'ACLU — Know Your Rights Lockscreen Graphics',
      description: 'Downloadable phone lockscreen images with your rights during police and ICE encounters. Available in English and Spanish — set as your wallpaper for instant reference.',
      url: 'https://www.aclu.org/know-your-rights/what-do-if-youre-stopped-police-infographic',
      category: 'know-your-rights',
    },
    {
      id: 'nat-k-8',
      name: 'CLINIC Rapid Response Toolkit',
      description: 'Comprehensive toolkit from the Catholic Legal Immigration Network for community response to immigration enforcement. Includes know-your-rights materials, family preparedness plans, and organizing guides.',
      url: 'https://www.cliniclegal.org/toolkits/rapid-response-toolkit',
      category: 'know-your-rights',
    },
  ],
};

export const ALL_STATES = [
  { value: 'alabama', label: 'Alabama' },
  { value: 'alaska', label: 'Alaska' },
  { value: 'arizona', label: 'Arizona' },
  { value: 'arkansas', label: 'Arkansas' },
  { value: 'california', label: 'California' },
  { value: 'colorado', label: 'Colorado' },
  { value: 'connecticut', label: 'Connecticut' },
  { value: 'delaware', label: 'Delaware' },
  { value: 'dc', label: 'District of Columbia' },
  { value: 'florida', label: 'Florida' },
  { value: 'georgia', label: 'Georgia' },
  { value: 'hawaii', label: 'Hawaii' },
  { value: 'idaho', label: 'Idaho' },
  { value: 'illinois', label: 'Illinois' },
  { value: 'indiana', label: 'Indiana' },
  { value: 'iowa', label: 'Iowa' },
  { value: 'kansas', label: 'Kansas' },
  { value: 'kentucky', label: 'Kentucky' },
  { value: 'louisiana', label: 'Louisiana' },
  { value: 'maine', label: 'Maine' },
  { value: 'maryland', label: 'Maryland' },
  { value: 'massachusetts', label: 'Massachusetts' },
  { value: 'michigan', label: 'Michigan' },
  { value: 'minnesota', label: 'Minnesota' },
  { value: 'mississippi', label: 'Mississippi' },
  { value: 'missouri', label: 'Missouri' },
  { value: 'montana', label: 'Montana' },
  { value: 'nebraska', label: 'Nebraska' },
  { value: 'nevada', label: 'Nevada' },
  { value: 'newhampshire', label: 'New Hampshire' },
  { value: 'newjersey', label: 'New Jersey' },
  { value: 'newmexico', label: 'New Mexico' },
  { value: 'newyork', label: 'New York' },
  { value: 'northcarolina', label: 'North Carolina' },
  { value: 'northdakota', label: 'North Dakota' },
  { value: 'ohio', label: 'Ohio' },
  { value: 'oklahoma', label: 'Oklahoma' },
  { value: 'oregon', label: 'Oregon' },
  { value: 'pennsylvania', label: 'Pennsylvania' },
  { value: 'rhodeisland', label: 'Rhode Island' },
  { value: 'southcarolina', label: 'South Carolina' },
  { value: 'southdakota', label: 'South Dakota' },
  { value: 'tennessee', label: 'Tennessee' },
  { value: 'texas', label: 'Texas' },
  { value: 'utah', label: 'Utah' },
  { value: 'vermont', label: 'Vermont' },
  { value: 'virginia', label: 'Virginia' },
  { value: 'washington', label: 'Washington' },
  { value: 'westvirginia', label: 'West Virginia' },
  { value: 'wisconsin', label: 'Wisconsin' },
  { value: 'wyoming', label: 'Wyoming' },
];

export const STATE_RESOURCES = {
  alabama: {
    name: 'Alabama',
    barAssociation: { name: 'Alabama State Bar', phone: '1-800-354-6154', url: 'https://www.alabar.org/public/lawyer-referral-service/' },
    organizations: [
      {
        id: 'al-1',
        name: 'Hispanic and Immigrant Center of Alabama (HICA)',
        description: 'Low-cost immigration legal services and naturalization assistance with DOJ-authorized representative on staff.',
        phone: '1-866-502-4422',
        url: 'https://hicaalabama.org',
        category: 'legal-aid',
        city: 'Birmingham',
        services: ['Citizenship', 'Visa assistance', 'DACA', 'Naturalization', 'Legal consultations'],
      },
      {
        id: 'al-2',
        name: 'Catholic Social Services of Montgomery',
        description: 'Low-cost family-based immigration services on a sliding scale. DOJ partial accredited representative.',
        phone: '(334) 288-8890',
        url: 'https://catholicsocialservices.org/legal-immigration-services',
        category: 'legal-aid',
        city: 'Montgomery',
        services: ['Family-based immigration', 'Consular processing', 'DACA', 'Citizenship'],
      },
      {
        id: 'al-3',
        name: 'Legal Services Alabama',
        description: 'Free civil legal aid to qualifying low-income families across all 67 Alabama counties.',
        phone: '1-866-456-4995',
        url: 'https://www.alabamalegalhelp.org',
        category: 'legal-aid',
        city: 'Statewide',
        services: ['Civil legal aid', 'Immigration matters'],
        languages: ['English', 'Spanish (888-835-3505)'],
      },
    ],
  },
  alaska: {
    name: 'Alaska',
    barAssociation: { name: 'Alaska Bar Association', phone: '1-907-272-7469', url: 'https://alaskabar.org/lawyer-referral-service/' },
    organizations: [
      {
        id: 'ak-1',
        name: 'Alaska Institute for Justice — Immigration Justice Project',
        description: 'Low-cost immigration legal services across all immigration application types. Free services for trafficking survivors.',
        phone: '(907) 279-2457',
        url: 'https://www.akijp.org',
        category: 'legal-aid',
        city: 'Anchorage',
        services: ['Citizenship', 'Work permits', 'Family petitions', 'Asylum', 'Trafficking survivor assistance'],
      },
      {
        id: 'ak-2',
        name: 'Immigrant Connection Alaska',
        description: 'Church-based, low-cost immigration legal services for a wide variety of immigration matters.',
        phone: null,
        url: 'https://www.awakenalaska.com/immigrant-connection',
        category: 'community',
        city: 'Anchorage',
        services: ['Family petitions', 'Naturalization', 'DACA', 'TPS', 'Consular processing'],
      },
      {
        id: 'ak-3',
        name: 'Alaska Legal Services Corporation',
        description: 'Free civil legal services to low-income Alaskans including some immigration matters.',
        phone: null,
        url: 'https://www.alsc-law.org',
        category: 'legal-aid',
        city: 'Statewide',
        services: ['Civil legal services'],
      },
    ],
  },
  arizona: {
    name: 'Arizona',
    barAssociation: { name: 'State Bar of Arizona', phone: '1-602-340-7239', url: 'https://www.azbar.org/for-the-public/lawyer-referral-service/' },
    organizations: [
      {
        id: 'az-1',
        name: 'Florence Immigrant & Refugee Rights Project',
        description: 'Free legal services for immigrants detained by ICE at Florence and Eloy detention centers.',
        phone: null,
        url: 'https://firrp.org',
        category: 'legal-aid',
        city: 'Florence',
        services: ['Detention defense', 'Legal orientations', 'Pro se assistance'],
      },
      {
        id: 'az-2',
        name: 'Phoenix Legal Action Network (PLAN)',
        description: 'Free legal services for non-detained immigrants facing deportation in Phoenix Immigration Court.',
        phone: null,
        url: 'https://planphx.org',
        category: 'legal-aid',
        city: 'Phoenix',
        services: ['Deportation defense'],
      },
      {
        id: 'az-3',
        name: 'Southern Arizona Legal Aid (SALA)',
        description: 'Free civil legal services including immigration for crime victims.',
        phone: null,
        url: 'https://www.sazlegalaid.org',
        category: 'legal-aid',
        city: 'Tucson',
        services: ['Immigration for crime victims', 'Civil legal services'],
      },
      {
        id: 'az-4',
        name: 'Friendly House — Immigration Department',
        description: 'Immigration legal services in the Phoenix area.',
        phone: '(602) 257-1870',
        url: 'https://friendlyhouse.org/immigration-legal-services/',
        category: 'legal-aid',
        city: 'Phoenix',
        services: ['Immigration legal services'],
      },
      {
        id: 'az-5',
        name: 'University of Arizona Immigration Law Clinic',
        description: 'Legal representation for low-income individuals with deportation defenses.',
        phone: null,
        url: 'https://law.arizona.edu/academics/clinics/immigration-law-clinic',
        category: 'legal-aid',
        city: 'Tucson',
        services: ['Deportation defense'],
      },
    ],
  },
  arkansas: {
    name: 'Arkansas',
    barAssociation: { name: 'Arkansas Bar Association', phone: '1-501-375-4606', url: 'https://www.arkbar.com/' },
    organizations: [
      {
        id: 'ar-1',
        name: 'University of Arkansas School of Law — Immigration Clinic',
        description: 'Free legal services for immigration-related legal issues through supervised law students.',
        phone: '(479) 575-3056',
        url: 'https://law.uark.edu/service-outreach/clinics/immigration-clinic.php',
        category: 'legal-aid',
        city: 'Fayetteville',
        services: ['Immigration legal representation', 'Consultations'],
      },
      {
        id: 'ar-2',
        name: 'Catholic Charities of Arkansas — Immigration Services',
        description: 'Low-cost immigration counseling, legal assistance, and workshops.',
        phone: '(479) 927-1996',
        url: 'https://www.dolr.org/immigration-services',
        category: 'legal-aid',
        city: 'Springdale',
        services: ['Immigration counseling', 'Family-based petitions', 'Citizenship', 'DACA'],
      },
      {
        id: 'ar-3',
        name: 'Arkansas United',
        description: 'Legal and adult education services for immigrants across Arkansas. Operates Immigrant Resource Centers.',
        phone: '(479) 763-2822',
        url: 'https://arkansasunited.org',
        category: 'community',
        city: 'Fayetteville',
        services: ['Legal service facilitation', 'Advocacy', 'Adult education'],
      },
    ],
  },
  california: {
    name: 'California',
    barAssociation: { name: 'State Bar of California', phone: '1-866-442-2529', url: 'https://www.calbar.ca.gov/Public/Need-Legal-Help' },
    organizations: [
      {
        id: 'ca-1',
        name: 'Legal Aid Foundation of Los Angeles (LAFLA)',
        description: 'Immigration relief for low-income LA County residents, victims of crime assistance, deportation defense.',
        phone: '1-800-399-4529',
        url: 'https://lafla.org/get-help/immigration/',
        category: 'legal-aid',
        city: 'Los Angeles',
        services: ['Deportation defense', 'Crime victim assistance', 'Immigration relief'],
      },
      {
        id: 'ca-2',
        name: 'Asian Americans Advancing Justice — Southern California',
        description: 'Immigration legal services, know-your-rights workshops, and citizenship workshops.',
        phone: '1-888-349-9695',
        url: 'https://www.advancingjustice-la.org',
        category: 'legal-aid',
        city: 'Los Angeles',
        services: ['Immigration legal services', 'Know Your Rights', 'Citizenship workshops', 'DACA'],
      },
      {
        id: 'ca-3',
        name: 'Public Counsel',
        description: 'Pro bono immigration representation, children\'s rights, and community development.',
        phone: '(213) 385-2977',
        url: 'https://publiccounsel.org/services/immigration/',
        category: 'legal-aid',
        city: 'Los Angeles',
        services: ['Pro bono immigration', 'Children\'s rights'],
      },
      {
        id: 'ca-4',
        name: 'Central American Resource Center (CARECEN)',
        description: 'Immigration legal services for Central American and other immigrant communities.',
        phone: '(213) 385-7800',
        url: 'https://www.carecen-la.org',
        category: 'legal-aid',
        city: 'Los Angeles',
        services: ['Immigration legal services'],
      },
      {
        id: 'ca-5',
        name: 'Immigration Institute of the Bay Area (IIBA)',
        description: 'Pro bono attorney matching, citizenship, family petitions, and DACA assistance.',
        phone: null,
        url: 'https://iibayarea.org',
        category: 'legal-aid',
        city: 'San Francisco',
        services: ['Pro bono matching', 'Citizenship', 'Family petitions', 'DACA'],
      },
      {
        id: 'ca-6',
        name: 'Asian Law Caucus',
        description: 'Immigration, workers\' rights, and housing for Asian/Pacific Islander communities.',
        phone: '(415) 896-1701',
        url: 'https://www.asianlawcaucus.org',
        category: 'legal-aid',
        city: 'San Francisco',
        services: ['Immigration', 'Workers\' rights', 'Housing'],
      },
      {
        id: 'ca-7',
        name: 'ICWC Law',
        description: 'Non-profit immigration service group serving clients statewide.',
        phone: null,
        url: 'https://www.icwclaw.org',
        category: 'legal-aid',
        city: 'Statewide',
        services: ['U-Visas', 'T-Visas', 'VAWA', 'DACA', 'SIJ'],
      },
    ],
  },
  colorado: {
    name: 'Colorado',
    barAssociation: { name: 'Colorado Bar Association', phone: '1-303-831-5723', url: 'https://www.cobar.org/For-the-Public/Find-a-Lawyer' },
    organizations: [
      {
        id: 'co-1',
        name: 'Rocky Mountain Immigrant Advocacy Network (RMIAN)',
        description: 'Free legal services for detained immigrants and children/youth statewide.',
        phone: '(303) 433-2812',
        url: 'https://www.rmian.org',
        category: 'legal-aid',
        city: 'Westminster',
        services: ['Detention defense', 'Children/youth immigration services'],
      },
      {
        id: 'co-2',
        name: 'Justice and Mercy Legal Aid Center (JAMLAC)',
        description: 'Full civil legal representation, consultations, and advocacy for survivors and immigrants.',
        phone: null,
        url: 'https://jamlac.org',
        category: 'legal-aid',
        city: 'Denver',
        services: ['Legal representation', 'Consultations', 'Advocacy'],
      },
      {
        id: 'co-3',
        name: 'Colorado Immigration Legal Defense Fund',
        description: 'Free immigration legal services for Coloradans and those detained in Colorado.',
        phone: null,
        url: 'https://ona.colorado.gov/our-work/immigration-legal-defense-fund',
        category: 'legal-aid',
        city: 'Statewide',
        services: ['Deportation defense', 'Immigration legal services'],
      },
      {
        id: 'co-4',
        name: 'Lutheran Family Services Rocky Mountains',
        description: 'Free consultation clinics twice monthly for Denver residents. Naturalization, DACA renewals, family petitions.',
        phone: null,
        url: 'https://lfsrm.org/Immigration-Legal-Services',
        category: 'legal-aid',
        city: 'Denver',
        services: ['Naturalization', 'DACA renewals', 'Family petitions', 'Free clinics'],
      },
    ],
  },
  connecticut: {
    name: 'Connecticut',
    barAssociation: { name: 'CT Bar Association', phone: '1-860-525-6052', url: 'https://www.ctbar.org/public/lawyer-referral-service' },
    organizations: [
      {
        id: 'ct-1',
        name: 'Connecticut Institute for Refugees and Immigrants (CIRI)',
        description: 'Only statewide nonprofit DOJ-recognized to provide immigration services. Affordable legal assistance.',
        phone: '(203) 336-0141',
        url: 'https://cirict.org',
        category: 'legal-aid',
        city: 'Hartford',
        services: ['Family-based petitions', 'Citizenship', 'DACA', 'Asylum', 'U-Visas', 'Deportation defense'],
      },
      {
        id: 'ct-2',
        name: 'Building One Community',
        description: 'Comprehensive resource center with immigration attorney and trained accredited representatives.',
        phone: '(203) 674-8585',
        url: 'https://b1c.org/legal/',
        category: 'legal-aid',
        city: 'Stamford',
        services: ['Immigration legal services', 'ESL classes', 'Workforce development'],
      },
      {
        id: 'ct-3',
        name: 'Immigration Advocacy & Support Center (IASC)',
        description: 'Only nonprofit in Southeast CT with DOJ accreditation. Sliding scale fees.',
        phone: '(860) 629-7758',
        url: 'https://www.iascct.org',
        category: 'legal-aid',
        city: 'New London',
        services: ['Immigration/naturalization legal services', 'Consultations'],
      },
    ],
  },
  delaware: {
    name: 'Delaware',
    barAssociation: { name: 'Delaware State Bar', phone: '1-302-658-5279', url: 'https://www.dsba.org/public/lawyer-referral/' },
    organizations: [
      {
        id: 'de-1',
        name: 'Community Legal Aid Society (CLASI)',
        description: 'Delaware\'s oldest civil legal aid org. Free immigration help for DV survivors, trafficking victims, and abused children.',
        phone: '(302) 575-0660',
        url: 'https://www.declasi.org/immigration',
        category: 'legal-aid',
        city: 'Wilmington',
        services: ['U-Visas', 'T-Visas', 'SIJS', 'VAWA', 'Citizenship', 'DACA renewals'],
      },
      {
        id: 'de-2',
        name: 'La Esperanza Community Center',
        description: 'Only bicultural bilingual multi-service nonprofit in Sussex County. Free immigration programs for Hispanic families.',
        phone: '(302) 854-9262',
        url: 'https://www.laesperanzacenter.org',
        category: 'community',
        city: 'Georgetown',
        services: ['Immigration assistance', 'Family development', 'Victim services'],
      },
    ],
  },
  dc: {
    name: 'District of Columbia',
    barAssociation: { name: 'DC Bar', phone: '1-202-737-4700', url: 'https://www.dcbar.org/for-the-public/lawyer-referral-service' },
    organizations: [
      {
        id: 'dc-1',
        name: 'Ayuda',
        description: 'Legal, social, and language services for low-income immigrants. Over 50 years serving the DC area.',
        phone: '(202) 387-4848',
        url: 'https://ayuda.com',
        category: 'legal-aid',
        city: 'Washington',
        services: ['Immigration', 'Asylum', 'Juvenile cases', 'Family law for DV/SA survivors'],
      },
      {
        id: 'dc-2',
        name: 'Legal Aid DC',
        description: 'DC\'s oldest and largest civil legal services org. Humanitarian-based immigration relief for survivors.',
        phone: null,
        url: 'https://www.legalaiddc.org/immigration-law',
        category: 'legal-aid',
        city: 'Washington',
        services: ['Humanitarian immigration relief', 'Survivors of violence/crime/persecution'],
      },
      {
        id: 'dc-3',
        name: 'DC Bar Pro Bono Center — Immigration Clinic',
        description: 'Free quarterly immigration legal consultations with bilingual attorneys.',
        phone: '(202) 780-2573',
        url: 'https://www.dcbar.org/pro-bono/free-legal-help/help-for-individuals/immigration-legal-advice-clinic',
        category: 'legal-aid',
        city: 'Washington',
        services: ['Free legal consultations', 'Referrals'],
      },
      {
        id: 'dc-4',
        name: 'Amica Center for Immigrant Rights',
        description: 'Legal defense for immigrant children and adults facing detention and deportation. Formerly CAIR Coalition.',
        phone: '(202) 331-3320',
        url: 'https://amicacenter.org',
        category: 'legal-aid',
        city: 'Washington',
        services: ['Detention defense', 'Asylum', 'Pro bono attorney matching', 'Children\'s cases'],
      },
      {
        id: 'dc-5',
        name: 'Washington Lawyers\' Committee for Civil Rights',
        description: 'Protects civil rights of immigrants through direct services and advocacy. KYR materials in 7+ languages.',
        phone: '(202) 319-1000',
        url: 'https://www.washlaw.org/what-we-do/immigrant-justice/',
        category: 'know-your-rights',
        city: 'Washington',
        services: ['Immigrant justice', 'Know Your Rights', 'Community resources'],
      },
    ],
  },
  florida: {
    name: 'Florida',
    barAssociation: { name: 'Florida Bar', phone: '1-800-342-8011', url: 'https://www.floridabar.org/public/lrs/' },
    organizations: [
      {
        id: 'fl-1',
        name: 'Florida Immigrant Coalition (FLIC)',
        description: 'Citizenship applications, TPS, EAD renewals, and community organizing.',
        phone: null,
        url: 'https://floridaimmigrant.org/legal-services/',
        category: 'legal-aid',
        city: 'Statewide',
        services: ['Citizenship', 'TPS', 'EAD renewals', 'Community organizing'],
      },
      {
        id: 'fl-2',
        name: 'Americans for Immigrant Justice',
        description: 'Detention defense, children\'s legal program, and immigration legal services.',
        phone: '(305) 573-1106',
        url: 'https://aijustice.org',
        category: 'legal-aid',
        city: 'Miami',
        services: ['Detention defense', 'Children\'s legal program'],
      },
      {
        id: 'fl-3',
        name: 'Florida Justice For Our Neighbors (JFON)',
        description: 'Free immigration legal aid including citizenship, family petitions, asylum, VAWA, and SIJ.',
        phone: null,
        url: 'https://fljfon.org',
        category: 'legal-aid',
        city: 'Miami, Orlando, Tampa Bay',
        services: ['Citizenship', 'Family petitions', 'Asylum', 'VAWA', 'U-Visas', 'SIJ'],
      },
      {
        id: 'fl-4',
        name: 'CWS Florida Legal Department',
        description: 'Free legal services across multiple Florida locations.',
        phone: null,
        url: 'https://cwssoutheast.org/services/legal-services/',
        category: 'legal-aid',
        city: 'Miami, Fort Lauderdale, Tampa, Jacksonville',
        services: ['Immigration legal services'],
      },
    ],
  },
  georgia: {
    name: 'Georgia',
    barAssociation: { name: 'State Bar of Georgia', phone: '1-404-527-8700', url: 'https://www.gabar.org/forthepublic/lawyerreferralservice.cfm' },
    organizations: [
      {
        id: 'ga-1',
        name: 'Asian Americans Advancing Justice — Atlanta',
        description: 'Multilingual immigration legal services with sliding-scale fees and free humanitarian relief.',
        phone: '(404) 890-5655',
        url: 'https://www.advancingjustice-atlanta.org',
        category: 'legal-aid',
        city: 'Atlanta',
        services: ['Immigration legal services', 'Humanitarian relief'],
      },
      {
        id: 'ga-2',
        name: 'CASA Georgia',
        description: 'Free remote immigration legal consultations.',
        phone: '1-866-765-2272',
        url: 'https://wearecasa.org/legal-services-ga/',
        category: 'legal-aid',
        city: 'Atlanta',
        services: ['Free consultations'],
      },
      {
        id: 'ga-3',
        name: 'Catholic Charities Atlanta',
        description: 'Free and reduced-cost immigration legal services.',
        phone: '(678) 222-3920',
        url: 'https://catholiccharitiesatlanta.org/services/immigration-legal-services/',
        category: 'legal-aid',
        city: 'Atlanta',
        services: ['Immigration legal services'],
      },
      {
        id: 'ga-4',
        name: 'Atlanta Legal Aid Society',
        description: 'Free civil legal assistance including immigration matters.',
        phone: '(404) 524-5811',
        url: 'https://atlantalegalaid.org',
        category: 'legal-aid',
        city: 'Atlanta',
        services: ['Civil legal assistance', 'Immigration matters'],
      },
    ],
  },
  hawaii: {
    name: 'Hawaii',
    barAssociation: { name: 'Hawaii State Bar', phone: '1-808-537-1868', url: 'https://hsba.org/HSBA/FOR_THE_PUBLIC/Lawyer_Referral_Service.aspx' },
    organizations: [
      {
        id: 'hi-1',
        name: 'Hawaii Immigrant Justice Center',
        description: 'Free legal services to eligible immigrants. Conducts community outreach and free legal clinics.',
        phone: '(808) 536-4302',
        url: 'https://www.legalaidhawaii.org',
        category: 'legal-aid',
        city: 'Honolulu',
        services: ['DV victims', 'Trafficking survivors', 'Family reunification', 'Removal defense', 'Citizenship'],
        hours: 'Intake: Mon-Fri 9am-11:30am, 1pm-3:30pm',
      },
      {
        id: 'hi-2',
        name: 'UH Refugee & Immigration Law Clinic',
        description: 'Free specialized legal services from the William S. Richardson School of Law.',
        phone: null,
        url: 'https://immigrationclinic.law.hawaii.edu',
        category: 'legal-aid',
        city: 'Honolulu',
        services: ['Asylum', 'Immigration legal representation'],
      },
      {
        id: 'hi-3',
        name: 'The Legal Clinic (TLC)',
        description: 'Immigration legal services for those in removal proceedings or detained.',
        phone: '(808) 777-7071',
        url: 'https://www.tlchawaii.org',
        category: 'legal-aid',
        city: 'Honolulu',
        services: ['Removal defense', 'Detention representation', 'Know Your Rights'],
      },
    ],
  },
  idaho: {
    name: 'Idaho',
    barAssociation: { name: 'Idaho State Bar', phone: '1-208-334-4500', url: 'https://isb.idaho.gov/ilrs/' },
    organizations: [
      {
        id: 'id-1',
        name: 'Catholic Charities of Idaho',
        description: 'DOJ-recognized nonprofit offering low-cost immigration legal services statewide.',
        phone: '(208) 350-7488',
        url: 'https://www.ccidaho.org/immigrationservices',
        category: 'legal-aid',
        city: 'Boise',
        services: ['Family immigration', 'Crime victims', 'DACA', 'Citizenship'],
      },
      {
        id: 'id-2',
        name: 'International Rescue Committee (IRC) — Boise',
        description: 'High-quality, low-cost immigration legal services and citizenship assistance.',
        phone: '(208) 344-1792',
        url: 'https://www.rescue.org/united-states/boise-id',
        category: 'legal-aid',
        city: 'Boise',
        services: ['Adjustment of status', 'Family reunification', 'Naturalization', 'Employment authorization'],
        hours: 'Walk-ins: Tues 10am-1pm, Thurs 1pm-3pm',
      },
      {
        id: 'id-3',
        name: 'The Alliance of Idaho',
        description: 'Low-cost immigration legal services for families in Blaine County area.',
        phone: null,
        url: 'https://www.allianceofidaho.org',
        category: 'legal-aid',
        city: 'Blaine County',
        services: ['Low-cost immigration legal services'],
      },
    ],
  },
  illinois: {
    name: 'Illinois',
    barAssociation: { name: 'Illinois State Bar', phone: '1-800-922-8757', url: 'https://www.isba.org/public/lawyerfinder' },
    organizations: [
      {
        id: 'il-1',
        name: 'National Immigrant Justice Center (NIJC)',
        description: 'Comprehensive immigration legal services, detention defense, and policy advocacy.',
        phone: null,
        url: 'https://immigrantjustice.org',
        category: 'legal-aid',
        city: 'Chicago',
        services: ['Legal services', 'Detention defense', 'Policy advocacy'],
      },
      {
        id: 'il-2',
        name: 'Legal Aid Chicago',
        description: 'Free civil legal services including immigration for low-income Chicago residents.',
        phone: null,
        url: 'https://legalaidchicago.org',
        category: 'legal-aid',
        city: 'Chicago',
        services: ['Civil legal services', 'Immigration'],
      },
      {
        id: 'il-3',
        name: 'Illinois Coalition for Immigrant and Refugee Rights (ICIRR)',
        description: 'Citizenship assistance, immigrant integration, and advocacy. 24-hour family support hotline.',
        phone: '1-855-435-7693',
        url: 'https://www.icirr.org',
        category: 'community',
        city: 'Chicago',
        services: ['Citizenship', 'Advocacy', '24-hour family support'],
        hours: '24/7 (family support line)',
        languages: ['English', 'Spanish', 'Korean', 'Polish'],
      },
      {
        id: 'il-4',
        name: 'Latinos Progresando',
        description: 'Immigration legal services focused on Latino communities in Chicago.',
        phone: null,
        url: 'https://latinospro.org',
        category: 'legal-aid',
        city: 'Chicago',
        services: ['Immigration legal services'],
      },
    ],
  },
  indiana: {
    name: 'Indiana',
    barAssociation: { name: 'Indiana State Bar', phone: '1-317-269-2222', url: 'https://www.inbar.org/page/lawyer-referral-service' },
    organizations: [
      {
        id: 'in-1',
        name: 'Indiana Legal Services — Immigrants\' & Language Rights Center',
        description: 'Statewide center providing direct legal representation on qualifying immigration matters.',
        phone: '1-844-243-8570',
        url: 'https://www.indianalegalservices.org/ilrc/',
        category: 'legal-aid',
        city: 'Indianapolis',
        services: ['Adjustment of status', 'Asylum', 'Employment authorization', 'Family petitions', 'Naturalization', 'U-Visas', 'T-Visas'],
        hours: 'Mon-Thu 10am-2pm ET',
      },
      {
        id: 'in-2',
        name: 'Neighborhood Christian Legal Clinic — Immigrant Justice Program',
        description: 'Free legal services and education statewide with a dedicated immigrant justice program.',
        phone: '(317) 429-4131',
        url: 'https://www.nclegalclinic.org/immigrant-justice-program',
        category: 'legal-aid',
        city: 'Indianapolis',
        services: ['Green cards', 'Citizenship', 'Family reunification', 'Refugee assistance', 'Asylum'],
      },
    ],
  },
  iowa: {
    name: 'Iowa',
    barAssociation: { name: 'Iowa State Bar', phone: '1-515-243-3179', url: 'https://www.iowabar.org/page/findlawyer' },
    organizations: [
      {
        id: 'ia-1',
        name: 'Iowa Migrant Movement for Justice',
        description: 'Free or low-cost immigration legal services with monthly clinics across the state.',
        phone: '(515) 255-9809',
        url: 'https://www.iowammj.org',
        category: 'legal-aid',
        city: 'Des Moines',
        services: ['Full-range immigration legal services'],
      },
      {
        id: 'ia-2',
        name: 'Catholic Charities of the Archdiocese of Dubuque',
        description: 'Free immigration legal consultations and low-cost assistance.',
        phone: '(319) 364-7121',
        url: 'https://catholiccharitiesdubuque.org/immigration-legal-services',
        category: 'legal-aid',
        city: 'Cedar Rapids',
        services: ['Free consultations (Fridays)', 'Family-based immigration'],
        hours: 'Consultations: Fri 8:30am-2:30pm',
      },
      {
        id: 'ia-3',
        name: 'Iowa Legal Aid',
        description: 'High-quality legal services for low-income and underserved Iowa residents.',
        phone: null,
        url: 'https://www.iowalegalaid.org',
        category: 'legal-aid',
        city: 'Statewide',
        services: ['Civil legal aid', 'Immigration matters'],
      },
    ],
  },
  kansas: {
    name: 'Kansas',
    barAssociation: { name: 'Kansas Bar Association', phone: '1-785-234-5696', url: 'https://www.ksbar.org/page/lrs' },
    organizations: [
      {
        id: 'ks-1',
        name: 'La Luz Immigration Clinic — Catholic Charities',
        description: 'Full-service immigration law office. Free walk-in consultations every Wednesday morning.',
        phone: '(913) 906-8971',
        url: 'https://catholiccharitiesks.org/la-luz-immigration-clinic/',
        category: 'legal-aid',
        city: 'Kansas City',
        services: ['Full immigration legal services', 'Free consultations'],
        hours: 'Free walk-in: Wed 9am-12pm',
        languages: ['English', 'Spanish'],
      },
      {
        id: 'ks-2',
        name: 'Kansas Legal Services',
        description: 'Legal and mediation services for low and moderate income Kansans.',
        phone: '1-800-723-5953',
        url: 'https://www.kansaslegalservices.org',
        category: 'legal-aid',
        city: 'Statewide',
        services: ['Immigration legal assistance', 'Civil legal aid'],
      },
    ],
  },
  kentucky: {
    name: 'Kentucky',
    barAssociation: { name: 'Kentucky Bar Association', phone: '1-502-564-3795', url: 'https://www.kybar.org/page/LRS' },
    organizations: [
      {
        id: 'ky-1',
        name: 'Maxwell Street Legal Clinic',
        description: 'High-quality, low-cost immigration legal assistance for immigrant and refugee communities.',
        phone: '(859) 233-3840',
        url: 'https://www.kyequaljustice.org/maxlegalaid',
        category: 'legal-aid',
        city: 'Lexington',
        services: ['DACA', 'Family-based immigration', 'SIJS', 'T/U-Visa', 'VAWA', 'TPS', 'Citizenship'],
      },
      {
        id: 'ky-2',
        name: 'Kentucky Refugee Ministries',
        description: 'Comprehensive resettlement and support services for refugee and immigrant populations.',
        phone: '(502) 479-9180',
        url: 'https://kyrm.org',
        category: 'legal-aid',
        city: 'Louisville',
        services: ['Permanent residency', 'Travel documents', 'Employment authorization', 'Citizenship', 'Family petitions'],
      },
      {
        id: 'ky-3',
        name: 'Legal Aid of the Bluegrass',
        description: 'Civil legal assistance including immigration for low-income residents of northern and central Kentucky.',
        phone: '(859) 431-8200',
        url: 'https://lablaw.org',
        category: 'legal-aid',
        city: 'Covington',
        services: ['Immigration', 'Housing', 'Family law', 'Civil rights'],
      },
    ],
  },
  louisiana: {
    name: 'Louisiana',
    barAssociation: { name: 'Louisiana State Bar', phone: '1-800-421-5722', url: 'https://www.lsba.org/Public/LawyerReferral.aspx' },
    organizations: [
      {
        id: 'la-1',
        name: 'Immigration Services & Legal Advocacy (ISLA)',
        description: 'Defends immigrant communities through free pro bono deportation defense. Visits detention centers across LA and MS.',
        phone: '(504) 265-0416',
        url: 'https://www.islaimmigration.org',
        category: 'legal-aid',
        city: 'New Orleans',
        services: ['Pro bono deportation defense', 'Detention representation'],
      },
      {
        id: 'la-2',
        name: 'Catholic Charities of North Louisiana',
        description: 'Only nonprofit in North Louisiana DOJ-recognized for immigration legal assistance.',
        phone: '(318) 865-0200',
        url: 'https://www.ccnla.org/immigration/',
        category: 'legal-aid',
        city: 'Shreveport',
        services: ['Family-based applications', 'Citizenship', 'DACA', 'TPS', 'U-Visas'],
      },
      {
        id: 'la-3',
        name: 'Catholic Charities Archdiocese of New Orleans (CCANO)',
        description: 'Humanitarian assistance to migrants, refugees, and asylum-seekers through legal aid and counseling.',
        phone: '(504) 523-3755',
        url: 'https://www.ccano.org/immigration-services/',
        category: 'legal-aid',
        city: 'New Orleans',
        services: ['Legal representation', 'DV victim assistance', 'Refugee services'],
      },
      {
        id: 'la-4',
        name: 'Puentes New Orleans',
        description: 'Immigration legal services, workplace justice, and domestic violence services.',
        phone: '(504) 861-5595',
        url: 'https://www.puentesneworleans.org/immigration-and-legal-services',
        category: 'legal-aid',
        city: 'New Orleans',
        services: ['Immigration legal services', 'Workplace justice', 'DV services'],
      },
    ],
  },
  maine: {
    name: 'Maine',
    barAssociation: { name: 'Maine State Bar', phone: '1-207-622-7523', url: 'https://www.mainebar.org/page/LawyerReferral' },
    organizations: [
      {
        id: 'me-1',
        name: 'Immigrant Legal Advocacy Project (ILAP)',
        description: 'Maine\'s only statewide immigration legal services organization. Direct legal services and community education.',
        phone: '1-800-497-8505',
        url: 'https://ilapmaine.org',
        category: 'legal-aid',
        city: 'Portland',
        services: ['Immigration form assistance', 'Legal consultations', 'Immigration court representation'],
        languages: ['English', 'French', 'Portuguese', 'Lingala', 'Spanish'],
      },
      {
        id: 'me-2',
        name: 'Maine Immigrants\' Rights Coalition',
        description: 'Network of 100+ organizations. Provides resource hub and hotline connecting immigrants to services.',
        phone: '(207) 544-9989',
        url: 'https://maineimmigrantrights.org',
        category: 'community',
        city: 'Statewide',
        services: ['Resource referrals', 'Community organizing', 'Advocacy'],
      },
    ],
  },
  maryland: {
    name: 'Maryland',
    barAssociation: { name: 'Maryland State Bar', phone: '1-800-492-1964', url: 'https://www.msba.org/for-the-public/lawyer-referral-service/' },
    organizations: [
      {
        id: 'md-1',
        name: 'Catholic Charities of Baltimore — Esperanza Center',
        description: 'Immigration legal services, ESL classes, employment counseling, and healthcare for new immigrants.',
        phone: null,
        url: 'https://www.catholiccharities-md.org',
        category: 'legal-aid',
        city: 'Baltimore',
        services: ['Immigration legal services', 'ESL', 'Employment counseling', 'Healthcare'],
      },
      {
        id: 'md-2',
        name: 'Pro Bono Resource Center of Maryland (PBRC)',
        description: 'Immigration legal triage at Baltimore Immigration Court, consultations, and limited pro se assistance.',
        phone: null,
        url: 'https://probonomd.org',
        category: 'legal-aid',
        city: 'Baltimore',
        services: ['Immigration court triage', 'Consultations', 'Pro se assistance'],
      },
      {
        id: 'md-3',
        name: 'World Relief Baltimore',
        description: 'Immigration legal clinic for permanent residence, work permits, family petitions, and naturalization.',
        phone: null,
        url: 'https://worldrelief.org/baltimore/for-clients/ils/',
        category: 'legal-aid',
        city: 'Baltimore',
        services: ['Permanent residence', 'Work permits', 'Family petitions', 'Naturalization'],
      },
      {
        id: 'md-4',
        name: 'Maryland Immigrant Rights Coalition',
        description: 'Advocacy, community organizing, and resource connection for immigrants.',
        phone: null,
        url: 'https://marylandimmigrantrightscoalition.org',
        category: 'community',
        city: 'Statewide',
        services: ['Advocacy', 'Community organizing', 'Resource connection'],
      },
    ],
  },
  massachusetts: {
    name: 'Massachusetts',
    barAssociation: { name: 'Massachusetts Bar', phone: '1-617-654-0400', url: 'https://www.massbar.org/public/lawyer-referral-service' },
    organizations: [
      {
        id: 'ma-1',
        name: 'Greater Boston Legal Services (GBLS)',
        description: '90+ attorneys providing free legal assistance including immigration.',
        phone: null,
        url: 'https://www.gbls.org',
        category: 'legal-aid',
        city: 'Boston',
        services: ['Immigration', 'Housing', 'Benefits', 'Employment'],
      },
      {
        id: 'ma-2',
        name: 'De Novo: Center for Justice and Healing',
        description: 'Free legal assistance for low-income immigrants and asylum seekers.',
        phone: null,
        url: 'https://www.denovo.org/immigration',
        category: 'legal-aid',
        city: 'Boston',
        services: ['Asylum', 'VAWA', 'T/U-Visas', 'SIJ'],
      },
      {
        id: 'ma-3',
        name: 'Rian Immigrant Center',
        description: 'Free legal consultations and full case representation before USCIS.',
        phone: null,
        url: 'https://www.riancenter.org',
        category: 'legal-aid',
        city: 'Boston',
        services: ['Legal consultations', 'USCIS representation'],
      },
      {
        id: 'ma-4',
        name: 'City of Boston Immigration Clinics',
        description: 'Free 15-minute private consultations with volunteer immigration attorneys.',
        phone: '(617) 635-2980',
        url: 'https://www.boston.gov/departments/immigrant-advancement/legal-resources-immigrants',
        category: 'legal-aid',
        city: 'Boston',
        services: ['Free consultations'],
      },
      {
        id: 'ma-5',
        name: 'MIRA Coalition',
        description: 'Helpline for immigration questions and resource referrals statewide.',
        phone: null,
        url: 'https://www.miracoalition.org',
        category: 'community',
        city: 'Statewide',
        services: ['Helpline', 'Immigration referrals'],
      },
    ],
  },
  michigan: {
    name: 'Michigan',
    barAssociation: { name: 'State Bar of Michigan', phone: '1-800-968-0738', url: 'https://www.michbar.org/public/lawyerreferral' },
    organizations: [
      {
        id: 'mi-1',
        name: 'Southwest Detroit Immigrant and Refugee Center (SWIRC)',
        description: 'Largest provider of free/low-cost immigration legal services in SE Michigan.',
        phone: null,
        url: 'https://detimmigrantcenter.com',
        category: 'legal-aid',
        city: 'Detroit',
        services: ['Naturalization', 'DACA', 'Asylum', 'TPS', 'Family visas', 'Appeals'],
      },
      {
        id: 'mi-2',
        name: 'Michigan Immigrant Rights Center (MIRC)',
        description: 'Free immigration legal services across multiple Michigan counties.',
        phone: '(734) 239-6863',
        url: 'https://michiganimmigrant.org',
        category: 'legal-aid',
        city: 'Kalamazoo',
        services: ['Immigration legal services'],
      },
      {
        id: 'mi-3',
        name: 'International Institute of Metropolitan Detroit',
        description: 'Immigration assistance for various ethnic communities in the Detroit metro area.',
        phone: '(313) 871-8600',
        url: 'https://www.iimd.org',
        category: 'legal-aid',
        city: 'Detroit',
        services: ['Immigration assistance'],
      },
      {
        id: 'mi-4',
        name: 'Freedom House Detroit',
        description: 'Shelter and legal services for asylum seekers.',
        phone: '(313) 964-4320',
        url: 'https://freedomhousedetroit.org',
        category: 'legal-aid',
        city: 'Detroit',
        services: ['Shelter', 'Legal services for asylum seekers'],
      },
    ],
  },
  minnesota: {
    name: 'Minnesota',
    barAssociation: { name: 'Minnesota State Bar', phone: '1-612-752-6699', url: 'https://www.mnbar.org/public/find-a-lawyer' },
    organizations: [
      {
        id: 'mn-1',
        name: 'Immigrant Law Center of Minnesota (ILCM)',
        description: 'Free immigration legal representation for low-income immigrants and refugees in MN, ND, and SD.',
        phone: '1-800-223-1368',
        url: 'https://www.ilcm.org',
        category: 'legal-aid',
        city: 'Saint Paul',
        services: ['Asylum', 'Deportation defense', 'Family petitions', 'Citizenship', 'DACA', 'TPS', 'VAWA'],
      },
      {
        id: 'mn-2',
        name: 'Mid-Minnesota Legal Aid — Immigration Law Project',
        description: 'Direct legal services and community clinics at multiple locations.',
        phone: null,
        url: 'https://mylegalaid.org/how-we-help/immigration/',
        category: 'legal-aid',
        city: 'Minneapolis',
        services: ['Immigration legal services', 'Community clinics'],
      },
      {
        id: 'mn-3',
        name: 'Volunteer Lawyers Network',
        description: 'Free immigration legal assistance for qualifying low-income individuals.',
        phone: '(612) 752-6677',
        url: 'https://www.vlnmn.org',
        category: 'legal-aid',
        city: 'Minneapolis',
        services: ['Pro bono immigration assistance'],
        hours: 'Intake: Mon-Thu 10am-1pm',
      },
    ],
  },
  mississippi: {
    name: 'Mississippi',
    barAssociation: { name: 'Mississippi Bar', phone: '1-601-948-4471', url: 'https://www.msbar.org/for-the-public/lawyer-referral-service/' },
    organizations: [
      {
        id: 'ms-1',
        name: 'El Pueblo',
        description: 'Professional low-cost humanitarian and family-based immigration legal services. Bilingual case management.',
        phone: '(228) 436-3986',
        url: 'https://elpueblo-ms.org',
        category: 'legal-aid',
        city: 'Biloxi',
        services: ['Family-based petitions', 'DACA', 'Naturalization', 'U-Visas', 'T-Visas', 'Pro bono representation'],
        hours: 'Mon-Thu 9am-5pm (by appointment)',
      },
      {
        id: 'ms-2',
        name: 'Mississippi Immigrants Rights Alliance (MIRA)',
        description: 'Statewide advocacy fighting for justice, healthcare, fair labor, and legal resources for immigrants.',
        phone: '(601) 968-5182',
        url: 'https://yourmira.org',
        category: 'community',
        city: 'Jackson',
        services: ['Immigration advocacy', 'Legal advocacy', 'Low/no-cost legal counsel'],
      },
    ],
  },
  missouri: {
    name: 'Missouri',
    barAssociation: { name: 'Missouri Bar', phone: '1-573-636-3635', url: 'https://www.mobar.org/lawyersearch.htm' },
    organizations: [
      {
        id: 'mo-1',
        name: 'Legal Services of Eastern Missouri — Immigration Law Project',
        description: 'Free civil legal assistance with extensive experience in immigrant crime victim cases.',
        phone: '1-800-444-0514',
        url: 'https://lsem.org',
        category: 'legal-aid',
        city: 'St. Louis',
        services: ['T-Visas', 'U-Visas', 'VAWA', 'Refugee/asylee adjustment', 'Naturalization'],
      },
      {
        id: 'mo-2',
        name: 'Legal Aid of Western Missouri',
        description: 'Wide range of legal services including a dedicated Immigration Law Project.',
        phone: '(816) 474-6750',
        url: 'https://lawmo.org',
        category: 'legal-aid',
        city: 'Kansas City',
        services: ['Citizenship', 'Family residence status', 'Green card renewal', 'Refugee/asylee residency'],
      },
      {
        id: 'mo-3',
        name: 'International Institute of St. Louis',
        description: 'Immigrant service hub since 1919 with comprehensive immigration legal services.',
        phone: '(314) 773-9090',
        url: 'https://www.iistl.org',
        category: 'legal-aid',
        city: 'St. Louis',
        services: ['Immigration legal services', 'Refugee adjustment services'],
      },
    ],
  },
  montana: {
    name: 'Montana',
    barAssociation: { name: 'State Bar of Montana', phone: '1-406-442-7660', url: 'https://www.montanabar.org/page/LawyerReferral' },
    organizations: [
      {
        id: 'mt-1',
        name: 'Montana Legal Services Association (MLSA)',
        description: 'Montana\'s primary free civil legal aid provider including immigration matters.',
        phone: '1-800-666-6899',
        url: 'https://www.mtlsa.org',
        category: 'legal-aid',
        city: 'Helena',
        services: ['Legal information', 'Referrals', 'Immigration consultations'],
        hours: 'Helpline: Tue-Thu 9am-1pm',
      },
      {
        id: 'mt-2',
        name: 'International Rescue Committee (IRC) — Missoula',
        description: 'Montana\'s only refugee resettlement agency. Immigration legal services from arrival through citizenship.',
        phone: null,
        url: 'https://www.rescue.org/united-states/missoula-mt',
        category: 'legal-aid',
        city: 'Missoula',
        services: ['Work authorization', 'Family reunification', 'Green cards', 'Citizenship', 'Refugee resettlement'],
      },
    ],
  },
  nebraska: {
    name: 'Nebraska',
    barAssociation: { name: 'Nebraska State Bar', phone: '1-402-475-7091', url: 'https://www.nebar.com/page/LRS' },
    organizations: [
      {
        id: 'ne-1',
        name: 'Center for Immigration and Refugee Advancement (CIRA)',
        description: 'Free civil immigration legal services for low-income immigrants. Bilingual hotline with 240+ language interpreters.',
        phone: '1-855-307-6730',
        url: 'https://ciraconnect.org',
        category: 'legal-aid',
        city: 'Omaha',
        services: ['Free legal screenings', 'Deportation defense', 'Asylum', 'Family petitions', 'DACA'],
      },
      {
        id: 'ne-2',
        name: 'Center for Legal Immigration Assistance (CLIA)',
        description: 'Pro bono and affordable immigration legal services since 2001.',
        phone: '(402) 471-1777',
        url: 'https://www.clianeb.org',
        category: 'legal-aid',
        city: 'Lincoln',
        services: ['Citizenship classes', 'Naturalization', 'Green card renewals', 'Family-based petitions', 'U-Visas'],
        languages: ['Arabic', 'Dari', 'English', 'Pashto', 'Spanish'],
      },
      {
        id: 'ne-3',
        name: 'Lutheran Family Services of Nebraska',
        description: 'Wide range of immigration legal services at affordable rates.',
        phone: '1-855-307-6730',
        url: 'https://www.onelfs.org/new-americans/immigration-legal-services/',
        category: 'legal-aid',
        city: 'Omaha',
        services: ['Family petitions', 'Citizenship', 'Permanent residence', 'TPS', 'VAWA'],
      },
    ],
  },
  nevada: {
    name: 'Nevada',
    barAssociation: { name: 'State Bar of Nevada', phone: '1-702-382-2200', url: 'https://www.nvbar.org/find-a-lawyer/' },
    organizations: [
      {
        id: 'nv-1',
        name: 'Legal Aid Center of Southern Nevada (LACSN)',
        description: 'Free civil legal services including immigration for low-income Southern Nevadans.',
        phone: '(702) 386-1070',
        url: 'https://www.lacsn.org/immigration',
        category: 'legal-aid',
        city: 'Las Vegas',
        services: ['Immigration legal services'],
      },
      {
        id: 'nv-2',
        name: 'Nevada Legal Services',
        description: 'Free legal services for residents at or below 200% federal poverty level.',
        phone: '(702) 836-0404',
        url: 'https://nevadalegalservices.org',
        category: 'legal-aid',
        city: 'Las Vegas',
        services: ['Civil legal services', 'Immigration'],
      },
      {
        id: 'nv-3',
        name: 'Immigration Center for Women and Children (ICWC) — Las Vegas',
        description: 'Free/affordable services for immigration humanitarian relief.',
        phone: '(702) 430-7981',
        url: 'https://www.icwclaw.org',
        category: 'legal-aid',
        city: 'Las Vegas',
        services: ['U-Visas', 'T-Visas', 'VAWA', 'Asylum', 'DACA', 'SIJ'],
      },
    ],
  },
  newhampshire: {
    name: 'New Hampshire',
    barAssociation: { name: 'NH Bar Association', phone: '1-603-229-0002', url: 'https://www.nhbar.org/lawyer-referral-service' },
    organizations: [
      {
        id: 'nh-1',
        name: 'Catholic Charities New Hampshire — Immigration Legal Services',
        description: 'High-quality, affordable immigration legal services.',
        phone: '(603) 889-9431',
        url: 'https://www.cc-nh.org/services/immigration-legal-services/',
        category: 'legal-aid',
        city: 'Nashua',
        services: ['Adjustment of status', 'Visas', 'Naturalization', 'Family reunification'],
      },
      {
        id: 'nh-2',
        name: 'International Institute of New England (IINE) — Manchester',
        description: 'Humanitarian relief, English language, employment support, and immigration legal services.',
        phone: '(603) 647-1500',
        url: 'https://iine.org',
        category: 'legal-aid',
        city: 'Manchester',
        services: ['Citizenship', 'Refugee resettlement', 'ESL', 'Employment support'],
      },
      {
        id: 'nh-3',
        name: 'New Hampshire Legal Assistance',
        description: 'Free civil legal aid for noncitizens facing removal or seeking immigration status.',
        phone: '1-800-639-5290',
        url: 'https://www.nhla.org/services',
        category: 'legal-aid',
        city: 'Statewide',
        services: ['Removal defense', 'TPS', 'Humanitarian parole'],
      },
    ],
  },
  newjersey: {
    name: 'New Jersey',
    barAssociation: { name: 'NJ State Bar', phone: '1-732-249-5000', url: 'https://tcms.njsba.com/PersonifyEbusiness/LegalResources/LawyerReferral.aspx' },
    organizations: [
      {
        id: 'nj-1',
        name: 'Legal Services of New Jersey (LSNJ)',
        description: 'Statewide legal aid including immigration. Pro bono panels available.',
        phone: '1-888-576-5529',
        url: 'https://www.lsnj.org',
        category: 'legal-aid',
        city: 'Statewide',
        services: ['Immigration legal assistance', 'Civil legal aid', 'Pro bono panels'],
        hours: 'Mon-Fri 8am-5:30pm',
      },
      {
        id: 'nj-2',
        name: 'Catholic Family & Community Services — Immigration Program',
        description: 'Consultation and representation for immigrant individuals and families.',
        phone: null,
        url: 'https://ccpaterson.org/legal-immigration-services',
        category: 'legal-aid',
        city: 'Central NJ',
        services: ['Consultation', 'Immigration representation'],
      },
      {
        id: 'nj-3',
        name: 'Casa Esperanza',
        description: 'Low-cost legal services and pro bono for low-income immigrants in Central NJ.',
        phone: null,
        url: 'http://www.casaesperanzanj.com',
        category: 'legal-aid',
        city: 'Central NJ',
        services: ['Low-cost legal services', 'Pro bono'],
      },
    ],
  },
  newmexico: {
    name: 'New Mexico',
    barAssociation: { name: 'State Bar of New Mexico', phone: '1-505-797-6066', url: 'https://www.sbnm.org/Public/Find-a-Lawyer' },
    organizations: [
      {
        id: 'nm-1',
        name: 'New Mexico Immigrant Law Center (NMILC)',
        description: 'Free collaborative legal services with holistic and trauma-informed representation.',
        phone: '(505) 247-1023',
        url: 'https://www.nmilc.org',
        category: 'legal-aid',
        city: 'Albuquerque',
        services: ['U-Visas', 'T-Visas', 'VAWA', 'DACA renewals', 'Citizenship', 'Detention representation'],
      },
      {
        id: 'nm-2',
        name: 'Catholic Charities of Central New Mexico',
        description: 'Low-cost immigration legal assistance for family-based cases.',
        phone: '(505) 539-0274',
        url: 'https://www.ccasfnm.org/immigration.html',
        category: 'legal-aid',
        city: 'Albuquerque',
        services: ['Family-based immigration', 'Adjustment of status', 'DV victim assistance (free)'],
      },
      {
        id: 'nm-3',
        name: 'ACLU of New Mexico — Regional Center for Border Rights',
        description: 'Immigration and border policy advocacy. Know Your Rights trainings in southern NM.',
        phone: '(575) 527-0664',
        url: 'https://www.aclu-nm.org',
        category: 'know-your-rights',
        city: 'Las Cruces',
        services: ['Know Your Rights trainings', 'Border rights defense', 'Policy reform'],
      },
    ],
  },
  newyork: {
    name: 'New York',
    barAssociation: { name: 'NY State Bar', phone: '1-800-342-3661', url: 'https://www.nysba.org/lawyerreferral/' },
    organizations: [
      {
        id: 'ny-1',
        name: 'NYC ActionNYC (Mayor\'s Office of Immigrant Affairs)',
        description: 'Free, safe immigration legal help for NYC residents. Call 311 and say "Immigration Legal."',
        phone: '1-800-354-0365',
        url: 'https://www.nyc.gov/site/immigrants/index.page',
        category: 'legal-aid',
        city: 'New York City',
        services: ['Free immigration legal help'],
        hours: 'Mon-Fri 9am-6pm',
      },
      {
        id: 'ny-2',
        name: 'The Legal Aid Society — Immigration Law Unit',
        description: '90+ staff delivering free immigration legal services. Runs the nation\'s first universal representation program for detained immigrants.',
        phone: null,
        url: 'https://legalaidnyc.org/get-help/immigration-deportation/',
        category: 'legal-aid',
        city: 'New York City',
        services: ['Deportation defense', 'Universal representation for detained immigrants'],
      },
      {
        id: 'ny-3',
        name: 'Legal Services NYC',
        description: 'Asylum, deportation defense, U-Visas, TPS, SIJS, and public benefits.',
        phone: null,
        url: 'https://www.legalservicesnyc.org',
        category: 'legal-aid',
        city: 'New York City',
        services: ['Asylum', 'Deportation defense', 'U-Visas', 'TPS', 'SIJS'],
      },
      {
        id: 'ny-4',
        name: 'New York Legal Assistance Group (NYLAG)',
        description: 'Comprehensive immigration legal services.',
        phone: null,
        url: 'https://nylag.org',
        category: 'legal-aid',
        city: 'New York City',
        services: ['Comprehensive immigration legal services'],
      },
      {
        id: 'ny-5',
        name: 'CUNY Citizenship Now!',
        description: 'Free and confidential immigration law services.',
        phone: null,
        url: 'https://www.cuny.edu/about/resources/citizenship-now/',
        category: 'legal-aid',
        city: 'New York City',
        services: ['Free immigration law services'],
      },
    ],
  },
  northcarolina: {
    name: 'North Carolina',
    barAssociation: { name: 'NC Bar Association', phone: '1-919-677-8574', url: 'https://www.ncbar.org/public-resources/find-an-attorney/' },
    organizations: [
      {
        id: 'nc-1',
        name: 'Legal Aid of North Carolina — IMMPAV',
        description: 'Free immigration assistance for survivors of domestic violence, sexual assault, and human trafficking.',
        phone: null,
        url: 'https://legalaidnc.org/project/immigration/',
        category: 'legal-aid',
        city: 'Statewide',
        services: ['Immigration for DV/SA/trafficking survivors'],
      },
      {
        id: 'nc-2',
        name: 'Charlotte Center for Legal Advocacy',
        description: 'Free civil and immigration legal assistance in the Charlotte area.',
        phone: null,
        url: 'https://charlottelegaladvocacy.org',
        category: 'legal-aid',
        city: 'Charlotte',
        services: ['Civil legal assistance', 'Immigration'],
      },
      {
        id: 'nc-3',
        name: 'Pisgah Legal Services',
        description: 'Free civil and immigration legal assistance in western North Carolina.',
        phone: null,
        url: 'https://www.pisgahlegal.org',
        category: 'legal-aid',
        city: 'Asheville',
        services: ['Civil legal assistance', 'Immigration'],
      },
    ],
  },
  northdakota: {
    name: 'North Dakota',
    barAssociation: { name: 'State Bar of ND', phone: '1-701-255-1404', url: 'https://www.sband.org/page/LawyerReferral' },
    organizations: [
      {
        id: 'nd-1',
        name: 'Immigrant Law Center of Minnesota (ILCM) — Fargo',
        description: 'Free immigration legal representation for low-income immigrants and refugees in MN, ND, and SD.',
        phone: '1-800-223-1368',
        url: 'https://www.ilcm.org',
        category: 'legal-aid',
        city: 'Fargo',
        services: ['Asylum', 'Deportation defense', 'Family petitions', 'Citizenship', 'DACA'],
      },
      {
        id: 'nd-2',
        name: 'The Advocates for Human Rights',
        description: 'Free legal help for low-income people detained or living in MN, ND, or SD.',
        phone: '(612) 341-9845',
        url: 'https://www.theadvocatesforhumanrights.org/legal_help',
        category: 'legal-aid',
        city: 'Serves from Minneapolis',
        services: ['Asylum', 'Deportation defense', 'Immigration court representation'],
      },
      {
        id: 'nd-3',
        name: 'Legal Services of North Dakota',
        description: 'Free legal assistance to low-income and elderly North Dakotans.',
        phone: null,
        url: 'https://lsnd.org',
        category: 'legal-aid',
        city: 'Statewide',
        services: ['Civil legal aid', 'Immigration matters'],
      },
    ],
  },
  ohio: {
    name: 'Ohio',
    barAssociation: { name: 'Ohio State Bar', phone: '1-800-282-6556', url: 'https://www.ohiobar.org/public-resources/lawyer-referral/' },
    organizations: [
      {
        id: 'oh-1',
        name: 'The Legal Aid Society of Cleveland',
        description: 'Immigration help for LPRs, DV survivors, trafficking victims, asylees, and refugees.',
        phone: null,
        url: 'https://lasclev.org/get-help/specific-populations/immigrants/',
        category: 'legal-aid',
        city: 'Cleveland',
        services: ['Immigration for LPRs', 'DV survivors', 'Trafficking victims', 'Asylees', 'Refugees'],
      },
      {
        id: 'oh-2',
        name: 'Community Refugee and Immigration Services (CRIS)',
        description: 'Pro bono immigration legal services in the Columbus area.',
        phone: '(614) 235-5747',
        url: 'https://www.crisohio.org',
        category: 'legal-aid',
        city: 'Columbus',
        services: ['Pro bono immigration services'],
      },
      {
        id: 'oh-3',
        name: 'Legal Aid of Southeast and Central Ohio',
        description: 'Free civil legal assistance for low-income residents.',
        phone: '(888) 246-4420',
        url: 'https://www.columbuslegalaid.org',
        category: 'legal-aid',
        city: 'Columbus',
        services: ['Civil legal assistance', 'Immigration'],
      },
    ],
  },
  oklahoma: {
    name: 'Oklahoma',
    barAssociation: { name: 'Oklahoma Bar', phone: '1-405-416-7000', url: 'https://www.okbar.org/public/lawyerreferral/' },
    organizations: [
      {
        id: 'ok-1',
        name: 'Catholic Charities of Eastern Oklahoma — Immigration Legal Services',
        description: 'Four immigration attorneys and two DOJ-accredited representatives.',
        phone: '(918) 508-7180',
        url: 'https://cceok.org/immigration/',
        category: 'legal-aid',
        city: 'Tulsa',
        services: ['Full immigration legal representation'],
        languages: ['English', 'Spanish', 'Burmese', 'Russian'],
      },
      {
        id: 'ok-2',
        name: 'Catholic Charities of Oklahoma City — Immigration Legal Services',
        description: 'Affordable immigration legal services for low-income individuals.',
        phone: '(405) 523-3001',
        url: 'https://catholiccharitiesok.org/immigration-legal-services',
        category: 'legal-aid',
        city: 'Oklahoma City',
        services: ['Family petitions', 'Political asylum', 'Deportation defense', 'VAWA', 'Citizenship'],
      },
      {
        id: 'ok-3',
        name: 'Legal Aid Services of Oklahoma',
        description: 'Free civil legal assistance statewide including immigration matters.',
        phone: '(888) 534-5243',
        url: 'https://legalaidok.org',
        category: 'legal-aid',
        city: 'Statewide',
        services: ['VAWA', 'U-Visa', 'T-Visa', 'Interpretation services'],
      },
    ],
  },
  oregon: {
    name: 'Oregon',
    barAssociation: { name: 'Oregon State Bar', phone: '1-503-684-3763', url: 'https://www.osbar.org/public/ris/' },
    organizations: [
      {
        id: 'or-1',
        name: 'Equity Corps of Oregon (ECO)',
        description: 'Publicly funded universal representation — free legal assistance for those in Portland Immigration Court.',
        phone: '(503) 360-0324',
        url: 'https://oregonlawhelp.org/referrals/equity-corps-oregon-eco-immigration-legal-services',
        category: 'legal-aid',
        city: 'Portland',
        services: ['Universal representation', 'Immigration court defense'],
      },
      {
        id: 'or-2',
        name: 'SOAR Immigration Legal Services',
        description: 'Representation for asylum, deportation defense, DACA, and family visas throughout Oregon.',
        phone: null,
        url: 'https://soarlegal.org',
        category: 'legal-aid',
        city: 'Statewide',
        services: ['Asylum', 'Deportation defense', 'DACA', 'Family visas'],
      },
      {
        id: 'or-3',
        name: 'Oregon Law Center',
        description: 'Free civil legal services including immigrants\' rights statewide.',
        phone: null,
        url: 'https://oregonlawcenter.org',
        category: 'legal-aid',
        city: 'Statewide',
        services: ['Immigrants\' rights', 'Civil legal services'],
      },
      {
        id: 'or-4',
        name: 'Pueblo Unido PDX',
        description: 'Legal navigation connecting immigrants to legal resources in Portland.',
        phone: null,
        url: 'https://www.pueblounidopdx.org',
        category: 'community',
        city: 'Portland',
        services: ['Legal navigation', 'Resource connection'],
      },
    ],
  },
  pennsylvania: {
    name: 'Pennsylvania',
    barAssociation: { name: 'PA Bar Association', phone: '1-800-692-7375', url: 'https://www.pabar.org/public/lfn/default.asp' },
    organizations: [
      {
        id: 'pa-1',
        name: 'Pennsylvania Immigration Resource Center (PIRC)',
        description: 'Free legal services for detained immigrants and those facing removal.',
        phone: null,
        url: 'https://www.pirclaw.org',
        category: 'legal-aid',
        city: 'York',
        services: ['Detention defense', 'Removal defense'],
      },
      {
        id: 'pa-2',
        name: 'Esperanza Immigration Legal Services',
        description: 'Free immigration legal services, education, and advocacy in North Philadelphia.',
        phone: null,
        url: 'https://www.esperanza.us/eils/',
        category: 'legal-aid',
        city: 'Philadelphia',
        services: ['Immigration legal services', 'Education', 'Advocacy'],
      },
      {
        id: 'pa-3',
        name: 'Nationalities Service Center (NSC)',
        description: 'Free legal services for detained immigrants, DV/trafficking survivors, and unaccompanied minors.',
        phone: null,
        url: 'https://nscphila.org',
        category: 'legal-aid',
        city: 'Philadelphia',
        services: ['Detention defense', 'DV/trafficking survivors', 'Unaccompanied minors'],
      },
      {
        id: 'pa-4',
        name: 'HIAS Pennsylvania',
        description: 'Immigration legal services in the Philadelphia area.',
        phone: null,
        url: 'https://hiaspa.org',
        category: 'legal-aid',
        city: 'Philadelphia',
        services: ['Immigration legal services'],
      },
    ],
  },
  rhodeisland: {
    name: 'Rhode Island',
    barAssociation: { name: 'RI Bar Association', phone: '1-401-421-5740', url: 'https://www.ribar.com/for-the-public/lawyer-referral-service' },
    organizations: [
      {
        id: 'ri-1',
        name: 'Dorcas International Institute of Rhode Island',
        description: 'Low-cost immigration legal services. 24/7 detention hotline available.',
        phone: '(401) 784-8607',
        url: 'https://www.diiri.org',
        category: 'legal-aid',
        city: 'Providence',
        services: ['Citizenship', 'Immigration representation', 'Refugee resettlement', 'ESL'],
      },
      {
        id: 'ri-2',
        name: 'Catholic Charities of Providence — Immigration Services',
        description: 'CLINIC member agency providing immigration legal services.',
        phone: '(401) 421-7833',
        url: 'https://dioceseofprovidence.org/immigration-refugee-services',
        category: 'legal-aid',
        city: 'Providence',
        services: ['Family petitions', 'Adjustment of status', 'Naturalization', 'TPS', 'DACA'],
        languages: ['English', 'Spanish', 'French', 'Tigrini', 'Amharic'],
      },
      {
        id: 'ri-3',
        name: 'Roger Williams University Immigration Clinic',
        description: 'Law students represent non-citizens in applications for relief from removal.',
        phone: '(401) 784-8784',
        url: 'https://law.rwu.edu/academics/juris-doctor/clinics-and-externships/immigration-clinic',
        category: 'legal-aid',
        city: 'Providence',
        services: ['Removal defense', 'Know Your Rights', 'Intake consultations'],
      },
    ],
  },
  southcarolina: {
    name: 'South Carolina',
    barAssociation: { name: 'SC Bar', phone: '1-803-799-6653', url: 'https://www.scbar.org/public/get-legal-help/' },
    organizations: [
      {
        id: 'sc-1',
        name: 'South Carolina Legal Services — Migrant Unit',
        description: 'Free legal assistance to migrant/seasonal farmworkers statewide including immigration law.',
        phone: '1-888-346-5592',
        url: 'https://sclegal.org/migrant/',
        category: 'legal-aid',
        city: 'Charleston',
        services: ['Immigration for crime victims', 'DV victims', 'Labor trafficking victims', 'Farmworker employment law'],
      },
      {
        id: 'sc-2',
        name: 'Catholic Charities of South Carolina',
        description: 'Immigration assistance across multiple SC locations.',
        phone: '(843) 388-0089',
        url: 'https://www.charitiessc.org',
        category: 'legal-aid',
        city: 'Charleston, Hilton Head, Greenville',
        services: ['Immigration assistance', 'Consultation'],
      },
    ],
  },
  southdakota: {
    name: 'South Dakota',
    barAssociation: { name: 'State Bar of SD', phone: '1-605-224-7554', url: 'https://www.statebarofsouthdakota.com/page/lawyerreferral' },
    organizations: [
      {
        id: 'sd-1',
        name: 'South Dakota Voices for Peace',
        description: 'Only organization in SD providing FREE immigration legal services. Focus on children and crime survivors.',
        phone: '(605) 782-9560',
        url: 'https://sdvfpeace.org',
        category: 'legal-aid',
        city: 'Sioux Falls',
        services: ['Free legal services for minors', 'Immigration for crime/DV survivors'],
        languages: ['English', 'Spanish'],
      },
      {
        id: 'sd-2',
        name: 'Lutheran Social Services of SD — Center for New Americans',
        description: 'Immigration legal services and integration support for refugees and immigrants.',
        phone: '(605) 731-2000',
        url: 'https://lsssd.org/what-we-do/center-for-new-americans/',
        category: 'legal-aid',
        city: 'Sioux Falls',
        services: ['Family reunification', 'Naturalization', 'Asylum', 'TPS', 'U-Visas', 'VAWA'],
      },
    ],
  },
  tennessee: {
    name: 'Tennessee',
    barAssociation: { name: 'Tennessee Bar', phone: '1-800-899-6993', url: 'https://www.tba.org/index.cfm?pg=lawyer-referral-service' },
    organizations: [
      {
        id: 'tn-1',
        name: 'Tennessee Justice for Our Neighbors (TNJFON)',
        description: 'Non-profit immigration law firm serving survivors of war, trafficking, and abuse.',
        phone: '(615) 538-7481',
        url: 'https://www.tnjfon.org',
        category: 'legal-aid',
        city: 'Nashville',
        services: ['U-Visa', 'T-Visa', 'VAWA', 'Asylum', 'USCIS representation', 'Immigration court'],
      },
      {
        id: 'tn-2',
        name: 'Tennessee Immigrant & Refugee Rights Coalition (TIRRC)',
        description: 'Statewide immigrant/refugee-led coalition providing legal services and advocacy.',
        phone: '(615) 414-1030',
        url: 'https://www.tnimmigrant.org',
        category: 'community',
        city: 'Nashville',
        services: ['DACA renewals', 'Naturalization', 'Removal defense', 'Advocacy', 'Referrals'],
      },
      {
        id: 'tn-3',
        name: 'Catholic Charities of Tennessee',
        description: 'Immigration counseling and form assistance by DOJ-accredited coordinator.',
        phone: '(615) 259-3567',
        url: 'https://cctenn.org/causes/new-americans/immigration-support-services/',
        category: 'legal-aid',
        city: 'Nashville',
        services: ['Immigration form assistance', 'Counseling', 'Refugee services'],
      },
    ],
  },
  texas: {
    name: 'Texas',
    barAssociation: { name: 'State Bar of Texas', phone: '1-800-252-9690', url: 'https://www.texasbar.com/AM/Template.cfm?Section=Lawyer_Referral_Service_702' },
    organizations: [
      {
        id: 'tx-1',
        name: 'RAICES (Refugee and Immigrant Center for Education and Legal Services)',
        description: 'Legal representation for immigrant families and refugees. Largest immigration legal services provider in Texas.',
        phone: '1-833-372-4237',
        url: 'https://raicestexas.org',
        category: 'legal-aid',
        city: 'San Antonio, Dallas, Houston',
        services: ['Detention defense', 'Family reunification', 'Asylum', 'Legal representation'],
      },
      {
        id: 'tx-2',
        name: 'Houston Immigration Legal Services Collaborative (HILSC)',
        description: 'Hotline staffed by volunteer attorneys with referrals and legal screenings.',
        phone: null,
        url: 'https://houstonimmigration.org',
        category: 'legal-aid',
        city: 'Houston',
        services: ['Attorney hotline', 'Legal screenings', 'Referrals'],
      },
      {
        id: 'tx-3',
        name: 'American Gateways',
        description: 'Asylum, VAWA, T/U-Visas, deportation defense, DACA, and naturalization in Central Texas.',
        phone: null,
        url: 'https://www.americangateways.org',
        category: 'legal-aid',
        city: 'Austin, San Antonio',
        services: ['Asylum', 'VAWA', 'T/U-Visas', 'Deportation defense', 'DACA', 'Naturalization'],
      },
      {
        id: 'tx-4',
        name: 'ACLU of Texas Immigrants\' Rights Hotline',
        description: 'Immigration rights hotline for Texas residents.',
        phone: '1-833-468-4664',
        url: 'https://www.aclutx.org/en/campaigns/hotline',
        category: 'hotline',
        city: 'Houston',
        services: ['Immigration rights hotline'],
        hours: 'Mon-Fri 9am-5pm',
      },
    ],
  },
  utah: {
    name: 'Utah',
    barAssociation: { name: 'Utah State Bar', phone: '1-801-531-9077', url: 'https://www.utahbar.org/public-services/lawyer-referral-service/' },
    organizations: [
      {
        id: 'ut-1',
        name: 'Utah Immigration Collaborative Helpline',
        description: 'Centralized referral network connecting immigrants to legal service providers.',
        phone: '(801) 382-9027',
        url: 'https://utahimmigration.wordpress.com',
        category: 'legal-aid',
        city: 'Salt Lake City',
        services: ['Legal screenings', 'Referrals'],
        hours: 'Mon-Fri 9am-5pm',
        languages: ['English', 'Spanish'],
      },
      {
        id: 'ut-2',
        name: 'Holy Cross Ministries',
        description: 'Free immigration legal representation for immigrants and families.',
        phone: '(801) 261-3440',
        url: 'https://www.hcmutah.org/legal-immigration-program/',
        category: 'legal-aid',
        city: 'Salt Lake City',
        services: ['Green cards', 'Citizenship', 'Asylum', 'U-Visa', 'DACA', 'Family reunification'],
        languages: ['English', 'Spanish'],
      },
      {
        id: 'ut-3',
        name: 'Comunidades Unidas',
        description: 'DOJ-recognized nonprofit providing low-cost immigration representation.',
        phone: '(801) 487-4143',
        url: 'https://www.cuutah.org/immigrationservices',
        category: 'legal-aid',
        city: 'Salt Lake City',
        services: ['Immigration status', 'Renewals', 'DACA', 'Naturalization'],
        hours: 'Mon-Thu 8am-6pm',
      },
      {
        id: 'ut-4',
        name: 'International Rescue Committee (IRC) — Salt Lake City',
        description: 'DOJ-recognized immigration legal assistance provider.',
        phone: '(801) 883-8461',
        url: 'https://www.rescue.org/united-states/salt-lake-city-ut',
        category: 'legal-aid',
        city: 'Salt Lake City',
        services: ['Citizenship', 'Green cards', 'Work authorization', 'Family reunification'],
      },
    ],
  },
  vermont: {
    name: 'Vermont',
    barAssociation: { name: 'Vermont Bar', phone: '1-802-223-2020', url: 'https://www.vtbar.org/FOR%20THE%20PUBLIC/Find%20a%20Lawyer/' },
    organizations: [
      {
        id: 'vt-1',
        name: 'Vermont Asylum Assistance Project (VAAP)',
        description: 'No-cost immigration legal assistance for noncitizens at risk of detention/deportation.',
        phone: '(802) 999-5654',
        url: 'https://www.vaapvt.org',
        category: 'legal-aid',
        city: 'Burlington',
        services: ['Asylum', 'Detention defense', 'Immigration status regularization'],
      },
      {
        id: 'vt-2',
        name: 'Vermont Legal Aid — Immigration Community Lawyering',
        description: 'Free immigration legal services, first in VLA\'s nearly 60-year history.',
        phone: '(802) 863-5620',
        url: 'https://www.vtlawhelp.org/immigration-and-citizenship',
        category: 'legal-aid',
        city: 'Burlington',
        services: ['Asylum', 'Green cards', 'Work permits'],
      },
      {
        id: 'vt-3',
        name: 'Association of Africans Living in Vermont (AALV)',
        description: 'Pro bono immigration legal assistance and integration services for new Americans.',
        phone: '(802) 985-3106',
        url: 'https://www.aalv-vt.org',
        category: 'legal-aid',
        city: 'Burlington',
        services: ['Adjustment of status', 'Asylum', 'Citizenship', 'Family reunification', 'TPS', 'U-Visas', 'VAWA'],
      },
    ],
  },
  virginia: {
    name: 'Virginia',
    barAssociation: { name: 'Virginia State Bar', phone: '1-804-775-0808', url: 'https://www.vsb.org/site/public/lawyer-referral-service' },
    organizations: [
      {
        id: 'va-1',
        name: 'Just Neighbors',
        description: 'Immigration legal services for low-income immigrants in Northern Virginia.',
        phone: '(703) 979-1240',
        url: 'https://www.justneighbors.org/',
        category: 'legal-aid',
        city: 'Northern Virginia',
        services: ['Immigration legal services'],
      },
      {
        id: 'va-2',
        name: 'CAIR Coalition (Capital Area Immigrants\' Rights)',
        description: 'Legal assistance for detained immigrants in the DC metro area.',
        phone: '(202) 331-3320',
        url: 'https://www.amicacenter.org/',
        category: 'legal-aid',
        city: 'DC Metro area',
        services: ['Detention defense'],
      },
      {
        id: 'va-3',
        name: 'Hogar Immigrant Services',
        description: 'Reduced-rate immigration legal assistance and monthly naturalization workshops.',
        phone: null,
        url: 'https://www.hogarimmigrantservices.org/',
        category: 'legal-aid',
        city: 'Northern Virginia',
        services: ['Immigration legal assistance', 'Naturalization workshops'],
      },
      {
        id: 'va-4',
        name: 'Virginia Legal Aid Statewide Hotline',
        description: 'Statewide legal aid hotline including immigration resources.',
        phone: '1-866-534-5243',
        url: 'https://www.valegalaid.org',
        category: 'legal-aid',
        city: 'Statewide',
        services: ['Legal aid', 'Immigration resources'],
      },
    ],
  },
  washington: {
    name: 'Washington',
    barAssociation: { name: 'Washington State Bar', phone: '1-206-443-9722', url: 'https://www.wsba.org/for-the-public/find-legal-help' },
    organizations: [
      {
        id: 'wa-1',
        name: 'Northwest Immigrant Rights Project (NWIRP)',
        description: 'Direct legal representation in immigration proceedings. 4 offices statewide.',
        phone: null,
        url: 'https://nwirp.org',
        category: 'legal-aid',
        city: 'Seattle',
        services: ['Immigration legal representation', 'Know Your Rights'],
      },
      {
        id: 'wa-2',
        name: 'Seattle OIRA Legal Defense Network',
        description: 'Legal advice and representation for low-income residents facing deportation.',
        phone: null,
        url: 'https://www.seattle.gov/iandraffairs/programs-and-services/immigration-legal-defense',
        category: 'legal-aid',
        city: 'Seattle',
        services: ['Deportation defense', 'Legal advice'],
      },
      {
        id: 'wa-3',
        name: 'Catholic Immigration Legal Services (CILS)',
        description: 'Free citizenship assistance and green card help for refugees/asylees.',
        phone: null,
        url: 'https://ccsww.org/services/catholic-immigration-legal-services/',
        category: 'legal-aid',
        city: 'Seattle, Mt. Vernon',
        services: ['Citizenship', 'Green cards for refugees/asylees'],
      },
      {
        id: 'wa-4',
        name: 'Columbia Legal Services',
        description: 'Free civil legal services including immigrant rights statewide.',
        phone: null,
        url: 'https://columbialegal.org',
        category: 'legal-aid',
        city: 'Statewide',
        services: ['Immigrant rights', 'Civil legal services'],
      },
    ],
  },
  westvirginia: {
    name: 'West Virginia',
    barAssociation: { name: 'WV State Bar', phone: '1-304-558-2456', url: 'https://wvbar.org/public-information/lawyer-referral-service/' },
    organizations: [
      {
        id: 'wv-1',
        name: 'Legal Aid of West Virginia',
        description: 'WV\'s primary provider of civil legal aid with 12 offices statewide.',
        phone: '1-866-255-4370',
        url: 'https://legalaidwv.org',
        category: 'legal-aid',
        city: 'Statewide',
        services: ['Civil legal aid', 'Immigration matters'],
      },
      {
        id: 'wv-2',
        name: 'WVU College of Law — Immigration Law Clinic',
        description: 'Free immigration legal services provided by law students since 1996.',
        phone: '(304) 293-7249',
        url: 'https://www.law.wvu.edu/clinical-law/clinics/immigration',
        category: 'legal-aid',
        city: 'Morgantown',
        services: ['Deportation defense', 'Asylum', 'Residency', 'DV protection', 'Refugee law'],
      },
      {
        id: 'wv-3',
        name: 'Catholic Charities West Virginia',
        description: 'Immigration legal services focused on family reunification and refugee resettlement.',
        phone: '(304) 343-1034',
        url: 'https://www.ccwva.org',
        category: 'legal-aid',
        city: 'Charleston',
        services: ['Adjustment of status', 'Asylum', 'DACA', 'Family-based petitions', 'TPS', 'U-Visas', 'Refugee resettlement'],
      },
    ],
  },
  wisconsin: {
    name: 'Wisconsin',
    barAssociation: { name: 'State Bar of Wisconsin', phone: '1-800-362-9082', url: 'https://www.wisbar.org/forPublic/INeedaLawyer/Pages/Lawyer-Referral-Service.aspx' },
    organizations: [
      {
        id: 'wi-1',
        name: 'Community Immigration Law Center (CILC)',
        description: 'Free legal representation for detained non-citizens and free immigration clinics.',
        phone: '(608) 640-4444',
        url: 'https://www.cilcmadison.org',
        category: 'legal-aid',
        city: 'Madison',
        services: ['Removal defense for detained', 'Free immigration clinics'],
        hours: 'Clinics: 2nd & 4th Fridays (by appointment)',
      },
      {
        id: 'wi-2',
        name: 'RISE Law Center / Centro Legal RISE',
        description: 'Multilingual nonprofit law office. Majority pro bono immigration and family law.',
        phone: '(608) 256-1015',
        url: 'https://riselawcenter.org',
        category: 'legal-aid',
        city: 'Madison',
        services: ['Immigration representation (statewide)', 'Family law', 'Protection orders'],
      },
      {
        id: 'wi-3',
        name: 'International Institute of Wisconsin',
        description: 'Low-cost immigration and citizenship services since 1936.',
        phone: '(414) 225-6220',
        url: 'https://iiwisconsin.org/immigration-services/',
        category: 'legal-aid',
        city: 'Milwaukee',
        services: ['Adjustment of status', 'Family petitions', 'Naturalization'],
        hours: 'Walk-ins: Fridays 9-11am',
        languages: ['Burmese', 'English', 'French', 'Korean', 'Russian', 'Spanish', 'Swahili', 'Urdu'],
      },
    ],
  },
  wyoming: {
    name: 'Wyoming',
    barAssociation: { name: 'Wyoming State Bar', phone: '1-307-632-9061', url: 'https://www.wyomingbar.org/for-the-public/lawyer-referral-service/' },
    organizations: [
      {
        id: 'wy-1',
        name: 'Wyoming Immigrant Advocacy Project',
        description: 'Wyoming\'s first and only comprehensive legal aid program for immigrants.',
        phone: null,
        url: 'https://wyomingiap.org',
        category: 'legal-aid',
        city: 'Jackson',
        services: ['Legal advice', 'Representation', 'Community education', 'Advocacy'],
      },
      {
        id: 'wy-2',
        name: 'Immigrant Hope — Wyoming/Idaho',
        description: 'Nonprofit immigration legal services for low-income clients in WY and Eastern ID.',
        phone: '(208) 709-0131',
        url: 'https://www.immigranthopewyomingidaho.org',
        category: 'legal-aid',
        city: 'Jackson',
        services: ['Green cards', 'Citizenship', 'DACA', 'Advance parole', 'Fiance visas'],
      },
      {
        id: 'wy-3',
        name: 'Catholic Charities of Wyoming',
        description: 'Immigration services including hotline, legal referrals, and Know Your Rights resources.',
        phone: '(307) 638-1530',
        url: 'https://charitieswyoming.org',
        category: 'community',
        city: 'Cheyenne',
        services: ['Immigration hotline', 'Legal referrals', 'Know Your Rights trainings', 'Advocacy'],
      },
    ],
  },
};

// ─── Specialty Filters ─────────────────────────────────────────
// Keyword-based filtering on the existing `services` arrays
export const SPECIALTY_FILTERS = [
  { id: 'all', labelKey: 'legalResponse.filterAll' },
  { id: 'immigration', labelKey: 'legalResponse.filterImmigration' },
  { id: 'criminal', labelKey: 'legalResponse.filterCriminal' },
  { id: 'protest', labelKey: 'legalResponse.filterProtest' },
];

const IMMIGRATION_KEYWORDS = [
  'immigration', 'citizenship', 'naturalization', 'daca', 'tps', 'asylum',
  'visa', 'u-visa', 't-visa', 'vawa', 'refugee', 'asylee', 'green card',
  'family petition', 'consular', 'adjustment of status', 'sij', 'sijs',
  'deportation', 'removal defense', 'detention', 'parole',
];

const CRIMINAL_KEYWORDS = [
  'deportation defense', 'detention defense', 'removal defense',
  'criminal defense', 'arrest', 'detention representation',
  'immigration court', 'pro bono deportation',
];

const PROTEST_KEYWORDS = [
  'know your rights', 'advocacy', 'community organizing', 'policy reform',
  'mass defense', 'protest', 'civil rights',
];

export function filterOrgsBySpecialty(orgs, specialty) {
  if (!specialty || specialty === 'all') return orgs;

  const keywords = specialty === 'immigration' ? IMMIGRATION_KEYWORDS
    : specialty === 'criminal' ? CRIMINAL_KEYWORDS
    : specialty === 'protest' ? PROTEST_KEYWORDS
    : [];

  return orgs.filter((org) => {
    const services = (org.services || []).join(' ').toLowerCase();
    const desc = (org.description || '').toLowerCase();
    const text = services + ' ' + desc;
    return keywords.some((kw) => text.includes(kw));
  });
}

// ─── NLG Protest / Mass Defense Resources (by state) ──────────
// National Lawyers Guild chapters for protest legal support
export const PROTEST_RESOURCES = {
  california: [
    { id: 'nlg-ca-1', name: 'NLG — Los Angeles', phone: '(323) 653-4510', city: 'Los Angeles' },
    { id: 'nlg-ca-2', name: 'NLG — San Francisco Bay Area', phone: '(415) 285-5067', city: 'San Francisco' },
  ],
  colorado: [
    { id: 'nlg-co-1', name: 'NLG — Colorado', phone: '(720) 771-4367', city: 'Denver' },
  ],
  'district of columbia': [
    { id: 'nlg-dc-1', name: 'NLG — DC Chapter', phone: '(202) 670-6584', city: 'Washington DC' },
  ],
  florida: [
    { id: 'nlg-fl-1', name: 'NLG — South Florida', phone: '(786) 363-2843', city: 'Miami' },
  ],
  georgia: [
    { id: 'nlg-ga-1', name: 'NLG — Atlanta', phone: '(678) 332-3943', city: 'Atlanta' },
  ],
  illinois: [
    { id: 'nlg-il-1', name: 'NLG — Chicago', phone: '(773) 309-1198', city: 'Chicago' },
  ],
  massachusetts: [
    { id: 'nlg-ma-1', name: 'NLG — Massachusetts', phone: '(617) 227-7335', city: 'Boston' },
  ],
  michigan: [
    { id: 'nlg-mi-1', name: 'NLG — Detroit/Michigan', phone: '(313) 963-0843', city: 'Detroit' },
  ],
  minnesota: [
    { id: 'nlg-mn-1', name: 'NLG — Minnesota', phone: '(612) 444-2654', city: 'Minneapolis' },
  ],
  'new york': [
    { id: 'nlg-ny-1', name: 'NLG — NYC', phone: '(212) 679-5100', city: 'New York' },
  ],
  ohio: [
    { id: 'nlg-oh-1', name: 'NLG — Northeast Ohio', phone: '(216) 736-8711', city: 'Cleveland' },
  ],
  oregon: [
    { id: 'nlg-or-1', name: 'NLG — Portland', phone: '(503) 902-5340', city: 'Portland' },
  ],
  pennsylvania: [
    { id: 'nlg-pa-1', name: 'NLG — Philadelphia', phone: '(267) 702-4654', city: 'Philadelphia' },
  ],
  texas: [
    { id: 'nlg-tx-1', name: 'NLG — Austin', phone: '(512) 480-8515', city: 'Austin' },
    { id: 'nlg-tx-2', name: 'NLG — Houston', phone: '(713) 543-2462', city: 'Houston' },
  ],
  washington: [
    { id: 'nlg-wa-1', name: 'NLG — Seattle', phone: '(206) 568-2977', city: 'Seattle' },
  ],
};
